import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { translateUi } from './i18n/index';
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  Bar,
  BarChart,
  Cell,
  Label,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CabPassScaledCardPreview } from "./CabPassScaledCardPreview";
import { buildCabPassVerifyUrl } from "./CabPassesTab";
import { DrivingCabPassCard } from "./DrivingCabPassCard";
import { cabPassCalendarToday } from "./drivingCabPassFormat";
import { LicenceCardScanAnimation } from "./LicenceCardScanAnimation";
import { Printer, Trash2 } from "./icons";
import { buildDataProtectionPrintNotice } from "./dataProtectionPrintNotice";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Centre caption drawn inside the Pie SVG so print/PDF snapshots keep label + ring aligned. */
function renderDonutHoleLabel(
  value: string | number,
  caption: string,
  valueFill: string,
  captionFill: string,
) {
  return (props: { viewBox?: unknown }) => {
    const viewBox = props.viewBox as { cx?: number; cy?: number } | undefined;
    const cx = viewBox?.cx ?? 0;
    const cy = viewBox?.cy ?? 0;
    return (
      <text textAnchor="middle" dominantBaseline="central">
        <tspan
          x={cx}
          y={cy - 6}
          fill={valueFill}
          fontSize={18}
          fontWeight={700}
          fontFamily="ui-sans-serif, system-ui, sans-serif"
        >
          {value}
        </tspan>
        <tspan
          x={cx}
          y={cy + 10}
          fill={captionFill}
          fontSize={7}
          fontWeight={600}
          letterSpacing="0.04em"
          fontFamily="ui-sans-serif, system-ui, sans-serif"
          style={{ textTransform: "uppercase" }}
        >
          {caption}
        </tspan>
      </text>
    );
  };
}

/* ------------------------------------------------------------------ */
/* Mirrors of server/verification/types.ts (kept in sync intentionally) */
/* ------------------------------------------------------------------ */

export type VerificationSeverity = "C" | "A" | "R" | "NA";

export type VerificationItemReference = { kind: string; id: string; label: string };

export type VerificationItem = {
  id: string;
  section: string;
  title: string;
  severity: VerificationSeverity;
  detail: string;
  references?: VerificationItemReference[];
};

export type VerificationSection = {
  id: string;
  label: string;
  items: VerificationItem[];
  stats?: Record<string, unknown>;
};

export type VerificationOverall = "pass" | "advisory" | "fail";

export type VerificationCounts = { C: number; A: number; R: number; NA: number };

export type DerogationRecord = {
  id: string;
  employee_id: string;
  employee_cycle_event_id?: string | null;
  event_name?: string | null;
  assessor_email?: string | null;
  assessor_name?: string | null;
  assessor_role?: string | null;
  assessor_tier_index?: number | null;
  assessor_tier_title?: string | null;
  assessee_role?: string | null;
  assessee_tier_index?: number | null;
  assessee_tier_title?: string | null;
  relationship: "same_tier" | "assessor_junior" | "late_assessment";
  reason: string;
  approved_by: string;
  filled_in_by_email?: string | null;
  filled_in_by_name?: string | null;
  created_at: string;
};

export type VerificationReport = {
  runId: string;
  employeeId: string;
  employeeName: string;
  employeeRole: string;
  employeeDepot: string;
  employeePayNo?: string | null;
  employeePhotoUrl?: string | null;
  companyCode: string;
  companyName?: string | null;
  runAt: string;
  runByEmail: string | null;
  runByName: string | null;
  overall: VerificationOverall;
  counts: VerificationCounts;
  sections: VerificationSection[];
  nonCompliance: VerificationItem[];
  derogations?: DerogationRecord[];
};

const SEVERITY_LABEL_EN: Record<VerificationSeverity, string> = {
  C: "Compliant",
  A: "Advisory",
  R: "Review",
  NA: "N/A",
};

function severityLabel(sev: VerificationSeverity): string {
  return translateUi(SEVERITY_LABEL_EN[sev]);
}

/** @deprecated Prefer severityLabel() so locale switches update labels. */
const SEVERITY_LABEL: Record<VerificationSeverity, string> = new Proxy(SEVERITY_LABEL_EN, {
  get(target, prop: string | symbol) {
    if (typeof prop === "string" && prop in target) {
      return severityLabel(prop as VerificationSeverity);
    }
    return Reflect.get(target, prop);
  },
}) as Record<VerificationSeverity, string>;

const SEVERITY_COLORS: Record<VerificationSeverity, { fg: string; bg: string; ring: string; chartHex: string }> = {
  C: {
    fg: "text-emerald-700",
    bg: "bg-emerald-50",
    ring: "ring-emerald-200",
    chartHex: "#10b981",
  },
  A: { fg: "text-amber-700", bg: "bg-amber-50", ring: "ring-amber-200", chartHex: "#f59e0b" },
  R: { fg: "text-red-700", bg: "bg-red-50", ring: "ring-red-200", chartHex: "#ef4444" },
  NA: { fg: "text-slate-600", bg: "bg-slate-100", ring: "ring-slate-200", chartHex: "#94a3b8" },
};

const SEVERITY_COLORS_DARK: Record<VerificationSeverity, { fg: string; bg: string; ring: string; chartHex: string }> = {
  C: { fg: "text-emerald-300", bg: "bg-emerald-500/15", ring: "ring-emerald-500/30", chartHex: "#34d399" },
  A: { fg: "text-amber-300", bg: "bg-amber-500/15", ring: "ring-amber-500/30", chartHex: "#fbbf24" },
  R: { fg: "text-rose-300", bg: "bg-rose-500/15", ring: "ring-rose-500/30", chartHex: "#fb7185" },
  NA: { fg: "text-slate-300", bg: "bg-slate-500/15", ring: "ring-slate-500/30", chartHex: "#94a3b8" },
};

/** Stable chart colours per verification section (used by the section donut). */
const SECTION_CHART_HEX: Record<string, string> = {
  main: "#6366f1",
  licence: "#0ea5e9",
  cab_passes: "#14b8a6",
  trains_routes: "#8b5cf6",
  experience: "#f59e0b",
  cycles: "#ec4899",
  training: "#10b981",
  incidents: "#ef4444",
  assessment_events: "#3b82f6",
  medical: "#84cc16",
  safety_briefs: "#f97316",
  driver_reports: "#06b6d4",
  open_tasks: "#64748b",
  leave_absence: "#0d9488",
  medication_checks: "#d97706",
};

const SECTION_CHART_HEX_FALLBACK = [
  "#6366f1",
  "#0ea5e9",
  "#14b8a6",
  "#8b5cf6",
  "#f59e0b",
  "#ec4899",
  "#10b981",
  "#ef4444",
  "#3b82f6",
  "#84cc16",
  "#f97316",
  "#64748b",
];

function sectionChartHex(sectionId: string, index: number): string {
  return SECTION_CHART_HEX[sectionId] ?? SECTION_CHART_HEX_FALLBACK[index % SECTION_CHART_HEX_FALLBACK.length];
}

type SectionBreakdownBar = {
  name: string;
  sectionId: string;
  C: number;
  A: number;
  R: number;
  NA: number;
};

function buildAggregateSectionBars(reports: VerificationReport[]): SectionBreakdownBar[] {
  const bySection = new Map<string, { label: string; C: number; A: number; R: number; NA: number }>();
  for (const report of reports) {
    for (const sec of report.sections) {
      const bucket = bySection.get(sec.id) ?? {
        label: translateUi(sec.label || VERIFICATION_SECTION_LABELS[sec.id] || sec.id),
        C: 0,
        A: 0,
        R: 0,
        NA: 0,
      };
      for (const item of sec.items) {
        bucket[item.severity] += 1;
      }
      bySection.set(sec.id, bucket);
    }
  }
  const orderedIds = [
    ...Object.keys(VERIFICATION_SECTION_LABELS).filter((id) => bySection.has(id)),
    ...[...bySection.keys()].filter((id) => !(id in VERIFICATION_SECTION_LABELS)),
  ];
  return orderedIds
    .map((id) => {
      const entry = bySection.get(id)!;
      return { sectionId: id, name: translateUi(entry.label), C: entry.C, A: entry.A, R: entry.R, NA: entry.NA };
    })
    .filter((row) => row.C + row.A + row.R + row.NA > 0);
}

/* ------------------------------------------------------------------ */
/* Utilities                                                           */
/* ------------------------------------------------------------------ */

/** UK long-date + short-time, e.g. "4 May 2026, 21:35". */
function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(String(iso));
  if (!Number.isFinite(d.getTime())) return String(iso);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** UK numeric date DD/MM/YYYY for date-only ISO strings — used in lightweight summaries. */
function formatDateUK(iso: string | null | undefined): string {
  if (!iso) return "—";
  const part = String(iso).split("T")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(part)) {
    const [y, m, d] = part.split("-");
    return `${d}/${m}/${y}`;
  }
  const d = new Date(String(iso));
  if (!Number.isFinite(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** Display total minutes as "Xh Ym" (engine emits the same format in details — we match it here). */
function formatHM(totalMinutes: number | null | undefined): string {
  const m = Math.max(0, Math.round(Number(totalMinutes) || 0));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h === 0) return `${mm}m`;
  if (mm === 0) return `${h}h`;
  return `${h}h ${mm}m`;
}

function severityChip(severity: VerificationSeverity, dark: boolean): React.ReactNode {
  const c = (dark ? SEVERITY_COLORS_DARK : SEVERITY_COLORS)[severity];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1",
        c.bg,
        c.fg,
        c.ring,
      )}
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: c.chartHex }} />
      {SEVERITY_LABEL[severity]}
    </span>
  );
}

const VERIFICATION_SECTION_LABELS: Record<string, string> = {
  main: "Main",
  licence: "Licence",
  cab_passes: "Cab Passes",
  trains_routes: "Trains & Routes",
  experience: "Experience Records",
  cycles: "Cycles",
  training: "Training",
  assessment_events: "Assessment events",
  medical: "Medical",
  safety_briefs: "Safety Briefs",
  driver_reports: "Driver Reports",
  incidents: "Incidents & CDP",
  open_tasks: "Open tasks",
  leave_absence: "Leave & Absence",
  medication_checks: "Medication Checks (OH)",
};

/** Where to send the user on the employee profile to resolve an advisory/review item. */
export type VerificationFixTarget = {
  item: VerificationItem;
  tab: string;
  sectionLabel: string;
  referenceLabel?: string;
};

export function resolveVerificationFixTarget(item: VerificationItem): VerificationFixTarget | null {
  if (item.severity !== "A" && item.severity !== "R") return null;

  const ref = item.references?.[0];
  let tab: string | undefined;

  if (ref) {
    const kind = ref.kind.toLowerCase();
    if (kind === "task") tab = "Tasks";
    else if (kind === "qualification") tab = "Training";
    else if (kind === "competency") tab = "Trains & Routes";
    else if (kind === "cycle" || kind === "cycle_event") tab = "Cycles";
    else if (kind === "incident" || kind === "cdp") tab = "Monitoring";
    else if (kind === "driver_report") tab = "Driver Reports";
    else if (kind === "oh_recommendation" || kind === "medication_check") tab = "Medication Checks";
  }

  if (!tab) {
    const sectionToTab: Record<string, string> = {
      main: "Main",
      licence: "Licence",
      cab_passes: "Cab Passes",
      trains_routes: "Trains & Routes",
      experience: "Experience Records",
      cycles: "Cycles",
      training: "Training",
      assessment_events: "Cycles",
      medical: "Medical",
      safety_briefs: "Safety Briefs",
      driver_reports: "Driver Reports",
      incidents: "Monitoring",
      open_tasks: "Tasks",
      leave_absence: "Leave & Absence",
      medication_checks: "Medication Checks",
    };
    tab = sectionToTab[item.section];
  }

  if (!tab) return null;

  return {
    item,
    tab,
    sectionLabel: translateUi(VERIFICATION_SECTION_LABELS[item.section] || item.section),
    referenceLabel: ref?.label,
  };
}

function VerificationFixButton({
  item,
  dark,
  onNavigateToFix,
}: {
  item: VerificationItem;
  dark: boolean;
  onNavigateToFix?: (target: VerificationFixTarget) => void;
}) {
  const target = resolveVerificationFixTarget(item);
  if (!target || !onNavigateToFix) return null;
  const isReview = item.severity === "R";
  const shortTab = target.tab === "Experience Records" ? "Experience" : target.tab;
  return (
    <button
      type="button"
      onClick={() => onNavigateToFix(target)}
      title={
        target.referenceLabel
          ? `Open ${target.tab} to fix: ${target.referenceLabel}`
          : `Open ${target.tab} to resolve this item`
      }
      className={cn(
        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap",
        isReview
          ? dark
            ? "bg-rose-600 text-white hover:bg-rose-500"
            : "bg-rose-600 text-white hover:bg-rose-700"
          : dark
            ? "bg-amber-600 text-white hover:bg-amber-500"
            : "bg-amber-600 text-white hover:bg-amber-700",
      )}
    >
      Fix in {shortTab} →
    </button>
  );
}

function overallVerdict(overall: VerificationOverall): { label: string; color: string } {
  switch (overall) {
    case "pass":
      return { label: "Verification passed", color: "#10b981" };
    case "advisory":
      return { label: "Verification with advice", color: "#f59e0b" };
    default:
      return { label: "Verification Failed", color: "#ef4444" };
  }
}

/* ------------------------------------------------------------------ */
/* SSE-driven scan + report runner hook                                */
/* ------------------------------------------------------------------ */

type ScanSectionProgress = {
  id: string;
  label: string;
  state: "pending" | "active" | "complete";
  /** 0–100 while scanning; 100 when complete. */
  percent: number;
};

type ScanProgress = {
  status: "idle" | "scanning" | "ready" | "error";
  /** Sections in order with state markers used by the scanning UI to highlight progress. */
  sections: ScanSectionProgress[];
  message?: string;
};

/**
 * After the server sends the final report, keep the scanning modal visible for at least this long
 * so users see the progress bar and section list settle (fast networks otherwise flash straight to
 * the report).
 */
const MIN_HOLD_MS_AFTER_STREAM_COMPLETE = 900;
/**
 * Minimum total time from starting the scan until we show the report — pads very fast verification
 * runs so the radar / section animation is observable.
 */
const MIN_TOTAL_SCAN_UI_MS = 3200;
/** Extra hold after the stream finishes (after min-total / min-hold) before swapping to the report. */
const EXTRA_PAUSE_MS_BEFORE_REPORT = 2000;

/** Core sections always shown while waiting for the server plan (addons added only when active). */
const CORE_SECTION_LIST: ScanSectionProgress[] = [
  { id: "main", label: "Main", state: "pending", percent: 0 },
  { id: "licence", label: "Licence", state: "pending", percent: 0 },
  { id: "cab_passes", label: "Cab Passes", state: "pending", percent: 0 },
  { id: "trains_routes", label: "Trains & Routes", state: "pending", percent: 0 },
  { id: "experience", label: "Experience Records", state: "pending", percent: 0 },
  { id: "cycles", label: "Cycles", state: "pending", percent: 0 },
  { id: "training", label: "Training & Qualifications", state: "pending", percent: 0 },
  { id: "incidents", label: "Incidents & CDP", state: "pending", percent: 0 },
  { id: "assessment_events", label: "Assessment Events", state: "pending", percent: 0 },
  { id: "medical", label: "Medical", state: "pending", percent: 0 },
];

function sectionsFromPlan(plan: { id: string; label: string }[]): ScanSectionProgress[] {
  return plan.map((s) => ({
    id: s.id,
    label: translateUi(s.label || VERIFICATION_SECTION_LABELS[s.id] || s.id),
    state: "pending" as const,
    percent: 0,
  }));
}

/** Map elapsed scan time onto per-tile pending → active(%) → complete. */
function paintSectionProgress(
  sections: ScanSectionProgress[],
  elapsedMs: number,
  totalMs: number,
): ScanSectionProgress[] {
  const n = sections.length;
  if (n === 0) return sections;
  const clampedTotal = Math.max(1, totalMs);
  const t = Math.min(1, Math.max(0, elapsedMs / clampedTotal));
  if (t >= 1) {
    return sections.map((s) => ({ ...s, state: "complete" as const, percent: 100 }));
  }
  const exact = t * n;
  const activeIndex = Math.min(n - 1, Math.floor(exact));
  const within = exact - activeIndex;
  return sections.map((s, i) => {
    if (i < activeIndex) return { ...s, state: "complete" as const, percent: 100 };
    if (i === activeIndex) {
      return { ...s, state: "active" as const, percent: Math.min(99, Math.max(1, Math.round(within * 100))) };
    }
    return { ...s, state: "pending" as const, percent: 0 };
  });
}

/**
 * Use fetch + stream reader rather than EventSource so we can pass `X-Company-Code` /
 * `X-User-Email` headers (matches every other authenticated API call in the app).
 */
async function* parseSseStream(reader: ReadableStreamDefaultReader<Uint8Array> ): AsyncGenerator <{ event: string; data: string }> {
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep = buffer.indexOf("\n\n");
    while (sep !== -1) {
      const chunk = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      let eventName = "message";
      let dataLines: string[] = [];
      for (const line of chunk.split("\n")) {
        if (line.startsWith("event:")) eventName = line.slice(6).trim();
        else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
      }
      if (dataLines.length > 0) {
        yield { event: eventName, data: dataLines.join("\n") };
      }
      sep = buffer.indexOf("\n\n");
    }
  }
}

export function useVerificationRun(opts: {
  employeeId: string;
  companyCode: string | undefined;
  currentUserEmail: string | undefined;
  /** Display name of the signed-in user (preferred over email on the report). */
  currentUserName?: string | undefined;
  /** Whether to start scanning immediately when triggered. */
  enabled: boolean;
}) {
  const { employeeId, companyCode, currentUserEmail, currentUserName, enabled } = opts;
  const [progress, setProgress] = useState<ScanProgress>({ status: "idle", sections: CORE_SECTION_LIST });
  const [report, setReport] = useState<VerificationReport | null>(null);
  const [persistedRunId, setPersistedRunId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const plannedSectionsRef = useRef<ScanSectionProgress[]>(CORE_SECTION_LIST);

  const start = useCallback(async () => {
    if (!companyCode || !employeeId) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    plannedSectionsRef.current = CORE_SECTION_LIST;
    setProgress({ status: "scanning", sections: CORE_SECTION_LIST });
    setReport(null);
    setPersistedRunId(null);
    const scanUiStartedAt = performance.now();
    /** Same overall on-screen duration as before (min total + extra pause). */
    const targetScanUiMs = MIN_TOTAL_SCAN_UI_MS + EXTRA_PAUSE_MS_BEFORE_REPORT;

    let animFrame = 0;
    const stopAnimation = () => {
      if (animFrame) cancelAnimationFrame(animFrame);
      animFrame = 0;
    };
    const tickAnimation = () => {
      if (controller.signal.aborted) {
        stopAnimation();
        return;
      }
      const elapsed = performance.now() - scanUiStartedAt;
      const painted = paintSectionProgress(plannedSectionsRef.current, elapsed, targetScanUiMs);
      setProgress((prev) => ({ ...prev, status: "scanning", sections: painted }));
      if (elapsed < targetScanUiMs) {
        animFrame = requestAnimationFrame(tickAnimation);
      } else {
        animFrame = 0;
      }
    };
    animFrame = requestAnimationFrame(tickAnimation);

    try {
      const url = `/api/employees/${encodeURIComponent(employeeId)}/verification/stream?companyCode=${encodeURIComponent(companyCode)}`;
      const displayName = (currentUserName || "").trim();
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "X-Company-Code": companyCode,
          ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
          ...(displayName && !displayName.includes("@") ? { "X-User-Name": displayName } : {}),
          Accept: "text/event-stream",
        },
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        const t = await res.text().catch(() => "");
        throw new Error(t || `Verification failed (${res.status})`);
      }
      const reader = res.body.getReader();
      let finalReport: VerificationReport | null = null;
      for await (const evt of parseSseStream(reader)) {
        if (controller.signal.aborted) break;
        let payload: Record<string, unknown> = {};
        try {
          payload = JSON.parse(evt.data);
        } catch {
          continue;
        }
        if (evt.event === "plan") {
          const plan = Array.isArray(payload.sections)
            ? (payload.sections as { id?: string; label?: string }[])
                .map((s) => ({ id: String(s.id ?? "").trim(), label: String(s.label ?? "").trim() }))
                .filter((s) => s.id)
            : [];
          if (plan.length > 0) {
            plannedSectionsRef.current = sectionsFromPlan(plan);
            const elapsed = performance.now() - scanUiStartedAt;
            setProgress({
              status: "scanning",
              sections: paintSectionProgress(plannedSectionsRef.current, elapsed, targetScanUiMs),
            });
          }
        } else if (evt.event === "complete") {
          finalReport = payload.report as VerificationReport;
        } else if (evt.event === "error") {
          throw new Error(String(payload.message || "Verification error"));
        }
        // section_started / section_complete intentionally ignored for UI pacing —
        // tiles animate 0→100% over the fixed scan window so fast servers still look sequential.
      }
      if (!finalReport) throw new Error("Verification ended without a report payload.");
      if (!(finalReport.runByName || "").trim() && displayName && !displayName.includes("@")) {
        finalReport = { ...finalReport, runByName: displayName };
      }

      /** Hold until the paced section animation finishes (keeps total time consistent). */
      const elapsedMs = performance.now() - scanUiStartedAt;
      const remainingAnimMs = Math.max(0, targetScanUiMs - elapsedMs);
      const pauseMs = Math.max(MIN_HOLD_MS_AFTER_STREAM_COMPLETE, remainingAnimMs);
      await new Promise<void>((resolve) => {
        let timeoutId: number | undefined;
        const finish = () => {
          if (timeoutId !== undefined) window.clearTimeout(timeoutId);
          controller.signal.removeEventListener("abort", onAbort);
          resolve();
        };
        timeoutId = window.setTimeout(finish, pauseMs);
        const onAbort = () => finish();
        controller.signal.addEventListener("abort", onAbort, { once: true });
      });
      if (controller.signal.aborted) return;

      stopAnimation();
      setProgress({
        status: "ready",
        sections: plannedSectionsRef.current.map((s) => ({ ...s, state: "complete", percent: 100 })),
      });
      setReport(finalReport);

      /* Persist so the run shows up in QA Verifications history. */
      try {
        const persistRes = await fetch(`/api/employees/${encodeURIComponent(employeeId)}/verification/runs`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Company-Code": companyCode,
            ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
          },
          body: JSON.stringify({ report: finalReport }),
        });
        const persistData = (await persistRes.json().catch(() => ({}))) as { runId?: string };
        if (persistRes.ok && persistData?.runId) {
          setPersistedRunId(persistData.runId);
          setReport((prev) => (prev ? { ...prev, runId: persistData.runId! } : prev));
        }
      } catch {
        /* persistence is best-effort — the on-screen report still works. */
      }
    } catch (err: unknown) {
      if (controller.signal.aborted) return;
      stopAnimation();
      const message = err instanceof Error ? err.message : translateUi("Verification failed");
      setProgress({ status: "error", sections: plannedSectionsRef.current, message });
    } finally {
      stopAnimation();
    }
  }, [companyCode, employeeId, currentUserEmail, currentUserName]);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    setProgress({ status: "idle", sections: CORE_SECTION_LIST });
    setReport(null);
  }, []);

  useEffect(() => {
    if (enabled && progress.status === "idle") {
      start();
    }
    if (!enabled) {
      abortRef.current?.abort();
    }
  }, [enabled, progress.status, start]);

  return { progress, report, persistedRunId, restart: start, cancel };
}

/* ------------------------------------------------------------------ */
/* Scan modal                                                          */
/* ------------------------------------------------------------------ */

export function VerificationScanModal({
  progress,
  isDarkMode,
  employeeName,
}: {
  progress: ScanProgress;
  isDarkMode?: boolean;
  employeeName: string;
}) {
  const dark = !!isDarkMode;
  const scanVisualState = progress.status === "error" ? "partial" : "scanning";
  const scanTitle =
    progress.status === "error" ? translateUi("Verification could not complete") : translateUi("Scanning employee record");
  const scanDescription =
    progress.status === "error"
      ? "Something went wrong while reading this record. Details below."
      : `Checking every section of ${employeeName || "this employee"}'s profile.`;

  const overallPercent = useMemo(() => {
    const list = progress.sections;
    if (!list.length) return 0;
    const sum = list.reduce((acc, s) => acc + (typeof s.percent === "number" ? s.percent : s.state === "complete" ? 100 : 0), 0);
    return Math.round(sum / list.length);
  }, [progress.sections]);

  return (
    <div
      className={cn(
        "rounded-3xl shadow-xl border p-8 w-full max-w-2xl mx-auto",
        dark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-slate-200 text-slate-800",
      )}
    >
      <LicenceCardScanAnimation
        state={scanVisualState}
        title={scanTitle}
        description={scanDescription}
        darkMode={dark}
        className="mb-6"
      />

      <h2 className={cn("text-lg font-bold text-center", dark ? "text-slate-200" : "text-slate-800")}>
        {translateUi("Running compliance verification")}
      </h2>
      <p className={cn("text-sm mt-1 text-center", dark ? "text-slate-400" : "text-slate-500")}>
        {translateUi("Section progress below updates as each part of the record is checked.")}
      </p>

      {progress.status === "error" && progress.message ? (
        <div
          className={cn(
            "mt-4 rounded-lg p-3 text-sm text-center",
            dark ? "bg-rose-500/10 text-rose-300 border border-rose-500/30" : "bg-red-50 text-red-700 border border-red-100",
          )}
        >
          {progress.message}
        </div>
      ) : null}

      <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {progress.sections.map((s) => (
          <li
            key={s.id}
            className={cn(
              "rounded-xl border px-3 py-2.5 transition-colors",
              s.state === "complete"
                ? dark
                  ? "border-emerald-400/30 bg-emerald-500/10"
                  : "border-emerald-200 bg-emerald-50"
                : s.state === "active"
                  ? dark
                    ? "border-indigo-400/40 bg-indigo-500/15"
                    : "border-indigo-200 bg-indigo-50"
                  : dark
                    ? "border-slate-700 bg-slate-800/40"
                    : "border-slate-200 bg-slate-50",
            )}
          >
            <div className="flex items-center gap-3">
              <span
                className={cn(
                  "h-2.5 w-2.5 shrink-0 rounded-full",
                  s.state === "complete"
                    ? "bg-emerald-500"
                    : s.state === "active"
                      ? cn("bg-indigo-500", "animate-pulse")
                      : dark
                        ? "bg-slate-600"
                        : "bg-slate-300",
                )}
              />
              <span className={cn("text-sm font-medium truncate", dark ? "text-slate-200" : "text-slate-800")}>
                {translateUi(s.label)}
              </span>
              <span className={cn("ml-auto text-xs tabular-nums shrink-0", dark ? "text-slate-400" : "text-slate-500")}>
                {s.state === "complete"
                  ? translateUi("Complete")
                  : s.state === "active"
                    ? `${s.percent}%`
                    : translateUi("Pending")}
              </span>
            </div>
            {s.state === "active" ? (
              <div
                className={cn(
                  "mt-2 h-1.5 w-full overflow-hidden rounded-full",
                  dark ? "bg-slate-700/80" : "bg-slate-200/80",
                )}
              >
                <div
                  className={cn("h-full rounded-full transition-[width] duration-100", dark ? "bg-indigo-400" : "bg-indigo-500")}
                  style={{ width: `${s.percent}%` }}
                />
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      <div
        className={cn(
          "mt-6 h-2 w-full overflow-hidden rounded-full",
          dark ? "bg-slate-800" : "bg-slate-100",
        )}
      >
        <div
          className={cn("h-full transition-[width] duration-150 ease-out", dark ? "bg-indigo-400" : "bg-indigo-500")}
          style={{ width: `${overallPercent}%` }}
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Report view                                                         */
/* ------------------------------------------------------------------ */

function StatCard({
  label,
  value,
  hint,
  dark,
  tone,
}: {
  label: string;
  value: string | number;
  hint?: string;
  dark: boolean;
  tone?: "default" | "good" | "warn" | "bad";
}) {
  const toneRing = (() => {
    switch (tone) {
      case "good":
        return dark ? "ring-emerald-500/30" : "ring-emerald-200";
      case "warn":
        return dark ? "ring-amber-500/30" : "ring-amber-200";
      case "bad":
        return dark ? "ring-rose-500/30" : "ring-rose-200";
      default:
        return dark ? "ring-slate-700" : "ring-slate-200";
    }
  })();
  return (
    <div className={cn("rounded-2xl p-4 ring-1 shadow-sm min-w-0", toneRing, dark ? "bg-slate-900/80" : "bg-white")}>
      <p className={cn("text-xs uppercase font-semibold tracking-wider break-words", dark ? "text-slate-400" : "text-slate-500")}>{label}</p>
      <p className={cn("mt-1 text-2xl font-bold break-words", dark ? "text-slate-100" : "text-slate-900")}>{value}</p>
      {hint ? <p className={cn("mt-1 text-xs break-words", dark ? "text-slate-500" : "text-slate-500")}>{hint}</p> : null}
    </div>
  );
}

function Pane({
  title,
  description,
  dark,
  children,
  rightSlot,
  lead,
  sectionPane,
  sectionsStart,
  afterSections,
  headerPane,
}: {
  title: string;
  description?: string;
  dark: boolean;
  children: React.ReactNode;
  rightSlot?: React.ReactNode;
  /** Optional block under the title (header pane: employee details beside the logo). */
  lead?: React.ReactNode;
  /** Marks a per-section details pane for print pagination hints. */
  sectionPane?: boolean;
  /** First section detail pane — forces a new printed page (page 2 after front matter). */
  sectionsStart?: boolean;
  /** Marks closing panes (non-compliance / derogations). */
  afterSections?: boolean;
  /** Employee/outcome header pane — compacted for print page 1. */
  headerPane?: boolean;
}) {
  return (
    <section
      data-qa-pane="true"
      data-qa-header-pane={headerPane ? "true" : undefined}
      data-qa-section-pane={sectionPane ? "true" : undefined}
      data-qa-sections-start={sectionsStart ? "true" : undefined}
      data-qa-after-sections={afterSections ? "true" : undefined}
      className={cn(
        "rounded-3xl shadow-sm border break-inside-avoid min-w-0",
        headerPane ? "p-2.5 sm:p-3" : "p-4 sm:p-5 lg:p-6",
        dark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200",
      )}
    >
      {headerPane ? (
        <div className="qa-header-top grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-x-3 sm:gap-x-4 gap-y-3 md:gap-y-2.5 items-start">
          <div className="qa-header-copy min-w-0 flex flex-col gap-2 md:col-start-1 md:row-start-1">
            <div className="min-w-0">
              <h3 className={cn("text-base sm:text-lg font-bold tracking-tight break-words leading-tight", dark ? "text-slate-100" : "text-slate-900")}>
                {title}
              </h3>
              {description ? (
                <p className={cn("text-sm mt-0.5 break-words leading-snug", dark ? "text-slate-400" : "text-slate-500")}>{description}</p>
              ) : null}
            </div>
            {lead}
          </div>
          {rightSlot ? (
            <div className="qa-export-brand-mark-wrap order-first md:order-none max-w-full min-w-0 shrink-0 self-start justify-self-start md:col-start-2 md:row-start-1 md:justify-self-end">
              {rightSlot}
            </div>
          ) : null}
          <div className="qa-header-outcome col-span-full min-w-0 w-full">{children}</div>
        </div>
      ) : (
        <>
          <header className="flex items-start justify-between gap-3 sm:gap-4 mb-4 min-w-0">
            <div className="min-w-0 flex-1">
              <h3 className={cn("text-base sm:text-lg font-bold tracking-tight break-words", dark ? "text-slate-100" : "text-slate-900")}>{title}</h3>
              {description ? <p className={cn("text-sm mt-0.5 break-words", dark ? "text-slate-400" : "text-slate-500")}>{description}</p> : null}
            </div>
            {rightSlot ? <div className="shrink-0">{rightSlot}</div> : null}
          </header>
          {lead}
          {children}
        </>
      )}
    </section>
  );
}

/** Per-item fix navigation for advisory/review rows when `onNavigateToFix` is provided. */
function ItemList({
  items,
  dark,
  onNavigateToFix,
}: {
  items: VerificationItem[];
  dark: boolean;
  onNavigateToFix?: (target: VerificationFixTarget) => void;
}) {
  if (items.length === 0) {
    return (
      <p className={cn("text-sm italic", dark ? "text-slate-400" : "text-slate-500")}>{translateUi("No checks ran for this section.")}</p>
    );
  }
  return (
    <div className="space-y-3">
      <ul className="space-y-2.5">
        {items.map((it) => {
          const c = (dark ? SEVERITY_COLORS_DARK : SEVERITY_COLORS)[it.severity];
          const showFix = (it.severity === "R" || it.severity === "A") && !!onNavigateToFix;
          return (
            <li
              key={it.id}
              className={cn(
                "rounded-xl border p-3.5 ring-1 ring-inset",
                c.bg,
                c.ring,
                dark ? "border-slate-700/60" : "border-slate-200",
              )}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap sm:flex-nowrap">
                <div className="min-w-0 flex-1">
                  <p className={cn("text-sm font-semibold break-words", dark ? "text-slate-100" : "text-slate-800")}>{it.title}</p>
                  <p className={cn("text-sm mt-0.5 break-words", dark ? "text-slate-300" : "text-slate-700")}>{it.detail}</p>
                  {it.references && it.references.length > 0 ? (
                    <p className={cn("mt-1.5 text-xs", dark ? "text-slate-400" : "text-slate-500")}>
                      {it.references.map((r) => `${r.kind}: ${r.label}`).join(" · ")}
                    </p>
                  ) : null}
                  {showFix ? (
                    <div className="mt-3 print:hidden">
                      <VerificationFixButton item={it} dark={dark} onNavigateToFix={onNavigateToFix} />
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  {severityChip(it.severity, dark)}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Specialised stats panel: experience records.
 * Layout (per user feedback May 2026):
 *   - Top row: five tiles (Type, Sessions, Total hours, Dark hours, Encounters) all sized equally.
 *   - Below: full-width "Encounters captured" bar chart with extra height so labels fit.
 */
function ExperienceStatsBlock({
  stats,
  dark,
  chartAnimate = true,
}: {
  stats: Record<string, unknown>;
  dark: boolean;
  /** Turn off only while preparing the print clone so the serialised SVG holds the final geometry. */
  chartAnimate?: boolean;
}) {
  const isTrainee = !!stats.isTrainee;
  const totalMinutes = Number(stats.totalMinutes ?? 0);
  const darkMinutes = Number(stats.darkMinutes ?? 0);
  const sessions = Number(stats.sessions ?? 0);
  const managerMinutes = Number(stats.managerTotalMinutes ?? 0);
  const minimumDark = Number(stats.minimumDarkMinutes ?? 0);
  const minimumManager = Number(stats.minimumManagerMinutes ?? 0);
  const managerPeriodMonths = Number(stats.managerMaintenancePeriodMonths ?? 0);
  const managerWindowStart = typeof stats.managerWindowStartYmd === "string" ? stats.managerWindowStartYmd.trim() : "";
  const encounters = Array.isArray((stats as { encounters?: unknown[] }).encounters)
    ? ((stats as { encounters: { label: string; count: number }[] }).encounters as { label: string; count: number }[])
    : [];

  /** Show all encounter types — chart is full-width with rotated labels so we no longer truncate at 8. */
  const barData = encounters.map((e) => ({ name: e.label, count: e.count }));
  const barAxisColor = dark ? "#94a3b8" : "#64748b";
  const barFill = dark ? "#818cf8" : "#6366f1";
  const tooltipStyle = dark
    ? { background: "#0f172a", border: "1px solid #334155", color: "#e2e8f0" }
    : undefined;

  /** Tone a tile based on whether its underlying value clears the company-standard minimum (when configured). */
  const darkTone: "good" | "bad" | "default" = minimumDark > 0 ? (darkMinutes >= minimumDark ? "good" : "bad") : "default";
  const managerTone: "good" | "bad" | "default" =
    minimumManager > 0 ? (managerMinutes >= minimumManager ? "good" : "bad") : "default";

  const managerHint =
    minimumManager > 0
      ? managerPeriodMonths > 0 && managerWindowStart
        ? `Min ${formatHM(minimumManager)} · ${managerPeriodMonths}-mo window`
        : `Min ${formatHM(minimumManager)}`
      : "Min not set";

  const totalEncounters = encounters.reduce((a, b) => a + b.count, 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {isTrainee ? (
          <>
            <StatCard label="Type" value={translateUi("Trainee")} dark={dark} />
            <StatCard label={translateUi("Sessions")} value={sessions} dark={dark} />
            <StatCard label={translateUi("Total hours")} value={formatHM(totalMinutes)} dark={dark} />
            <StatCard
              label="Dark hours"
              value={formatHM(darkMinutes)}
              dark={dark}
              tone={darkTone}
              hint={minimumDark > 0 ? `Min ${formatHM(minimumDark)}` : "Min not set"}
            />
            <StatCard label="Encounters" value={totalEncounters} dark={dark} />
          </>
        ) : (
          <>
            <StatCard label="Type" value="Qualified" dark={dark} />
            <StatCard label={translateUi("Sessions")} value={sessions} dark={dark} />
            <StatCard
              label={translateUi("Driving hours")}
              value={formatHM(managerMinutes)}
              dark={dark}
              tone={managerTone}
              hint={managerHint}
            />
            <StatCard label={translateUi("Total hours")} value={formatHM(totalMinutes)} dark={dark} />
            <StatCard label="Encounter types" value={encounters.length} dark={dark} />
          </>
        )}
      </div>
      <div className={cn("rounded-2xl ring-1 p-4 shadow-sm", dark ? "ring-slate-700 bg-slate-900/80" : "ring-slate-200 bg-white")}>
        <p className={cn("text-xs font-semibold uppercase tracking-wider", dark ? "text-slate-400" : "text-slate-500")}>
          {translateUi("Encounters captured")}
        </p>
        {barData.length === 0 ? (
          <p className={cn("text-sm mt-2 italic", dark ? "text-slate-400" : "text-slate-500")}>{translateUi("No encounters recorded.")}</p>
        ) : (
          <div className="qa-chart-host h-72 mt-3 min-h-[18rem]" data-qa-chart-export="experience-encounters">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} margin={{ top: 8, right: 16, left: 0, bottom: 80 }}>
                <XAxis
                  dataKey="name"
                  stroke={barAxisColor}
                  tick={{ fontSize: 11 }}
                  interval={0}
                  angle={-35}
                  textAnchor="end"
                  height={80}
                />
                <YAxis stroke={barAxisColor} allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill={barFill} radius={[4, 4, 0, 0]} isAnimationActive={chartAnimate} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

function AssessmentEventsStatsBlock({
  stats,
  dark,
  chartAnimate = true,
}: {
  stats: Record<string, unknown>;
  dark: boolean;
  chartAnimate?: boolean;
}) {
  const count = Number(stats.count ?? 0);
  const withEvidence = Number(stats.withEvidence ?? 0);
  const withPhotos = Number(stats.withPhotos ?? 0);
  const minimum = Number(stats.minimum ?? 0);
  const methods = (stats.methods as Record<string, number>) || {};
  const pieData = Object.entries(methods).map(([name, value]) => ({ name, value }));
  const palette = dark
    ? ["#818cf8", "#34d399", "#fbbf24", "#fb7185", "#22d3ee", "#a78bfa"]
    : ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#06b6d4", "#8b5cf6"];
  const tooltipStyle = dark
    ? { background: "#0f172a", border: "1px solid #334155", color: "#e2e8f0" }
    : undefined;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="grid grid-cols-2 gap-3 min-w-0">
        <StatCard label={translateUi("Events")} value={count} dark={dark} />
        <StatCard
          label="Min. required"
          value={minimum > 0 ? minimum : "—"}
          dark={dark}
          tone={minimum === 0 ? "default" : count >= minimum ? "good" : "bad"}
        />
        <StatCard
          label="With evidence"
          value={count > 0 ? `${Math.round((withEvidence / count) * 100)}%` : "—"}
          dark={dark}
          tone={count > 0 && withEvidence === count ? "good" : count > 0 && withEvidence === 0 ? "bad" : "warn"}
        />
        <StatCard
          label="With photos"
          value={count > 0 ? `${Math.round((withPhotos / count) * 100)}%` : "—"}
          dark={dark}
        />
      </div>
      <div className={cn("rounded-2xl ring-1 p-3 shadow-sm", dark ? "ring-slate-700 bg-slate-900/80" : "ring-slate-200 bg-white")}>
        <p className={cn("text-xs font-semibold uppercase tracking-wider", dark ? "text-slate-400" : "text-slate-500")}>
          {translateUi("Methods used")}
        </p>
        {pieData.length === 0 ? (
          <p className={cn("text-sm mt-2 italic", dark ? "text-slate-400" : "text-slate-500")}>{translateUi("No methods captured.")}</p>
        ) : (
          <div className="qa-chart-host h-44 mt-2 min-h-[11rem]" data-qa-chart-export="assessment-methods">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={28} outerRadius={56} isAnimationActive={chartAnimate}>
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={palette[i % palette.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend
                  verticalAlign="bottom"
                  height={28}
                  wrapperStyle={{ fontSize: 11, color: dark ? "#cbd5e1" : "#475569" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

/** Matches CabPassesTab `isPassDateRangeOk` — today within [validFrom, validTo] when dates are set. */
function cabPassDateRangeOk(validFrom: string, validTo: string): boolean {
  const today = cabPassCalendarToday();
  const vf = String(validFrom ?? "")
    .trim()
    .split("T")[0];
  const vt = String(validTo ?? "")
    .trim()
    .split("T")[0];
  if (vf && today < vf) return false;
  if (vt && today > vt) return false;
  return true;
}

function cabPassExpiresToday(validTo: string): boolean {
  const today = cabPassCalendarToday();
  const vt = String(validTo ?? "")
    .trim()
    .split("T")[0];
  return !!vt && today === vt;
}

type CabPassPreviewRow = {
  id: string;
  label: string;
  passNumber: string;
  color: string;
  validFrom: string;
  validTo: string;
  expired: boolean;
  signed: boolean;
  severity: VerificationSeverity;
  imageUrl: string | null;
  issuedBy: string;
  issuedByDept: string;
  validityScope: string;
  endorsements: string;
  issuerSignatureUrl: string | null;
  digital: boolean;
  verificationToken: string;
  scanCode: string;
};

function cabPassPreviewRowsFromStats(stats: Record<string, unknown>): CabPassPreviewRow[] {
  const raw = (stats as { passes?: unknown }).passes;
  if (!Array.isArray(raw)) return [];
  return raw.map((item, idx) => {
    const x = (item && typeof item === "object" ? item : {}) as Record<string, unknown>;
    const sevRaw = String(x.severity ?? "C");
    const severity = (["C", "A", "R", "NA"].includes(sevRaw) ? sevRaw : "C") as VerificationSeverity;
    const id = String(x.id ?? `pass-${idx}`);
    return {
      id,
      label: String(x.label ?? translateUi("Cab pass")),
      passNumber: String(x.passNumber ?? "").trim(),
      color: String(x.color ?? "green").trim().toLowerCase(),
      validFrom: String(x.validFrom ?? "").trim(),
      validTo: String(x.validTo ?? "").trim(),
      expired: Boolean(x.expired),
      signed: Boolean(x.signed),
      severity,
      imageUrl: typeof x.imageUrl === "string" && x.imageUrl.trim() ? x.imageUrl.trim() : null,
      issuedBy: String(x.issuedBy ?? "").trim(),
      issuedByDept: String(x.issuedByDept ?? "").trim(),
      validityScope: String(x.validityScope ?? "").trim(),
      endorsements: String(x.endorsements ?? "").trim(),
      issuerSignatureUrl:
        typeof x.issuerSignatureUrl === "string" && x.issuerSignatureUrl.trim()
          ? String(x.issuerSignatureUrl).trim()
          : null,
      digital: x.digital === true,
      verificationToken: String(x.verificationToken ?? "").trim(),
      scanCode: String(x.scanCode ?? "").trim().toLowerCase(),
    };
  });
}

/**
 * Cab passes on record: scaled driving cab pass card (same as the employee profile tab), plus
 * verification context (dates, signature, severity).
 */
function CabPassPreviewBlock({
  stats,
  dark,
  report,
}: {
  stats: Record<string, unknown>;
  dark: boolean;
  report: VerificationReport;
}) {
  const passes = cabPassPreviewRowsFromStats(stats);
  if (passes.length === 0) return null;
  const hasNonCompliant = passes.some((p) => p.severity !== "C");
  const colors = dark ? SEVERITY_COLORS_DARK : SEVERITY_COLORS;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return (
    <div
      className={cn(
        "rounded-2xl ring-1 p-4 shadow-sm",
        hasNonCompliant
          ? dark
            ? "ring-amber-500/30 bg-amber-500/5"
            : "ring-amber-200 bg-amber-50"
          : dark
            ? "ring-teal-500/30 bg-teal-500/5"
            : "ring-teal-200 bg-teal-50",
      )}
    >
      <p
        className={cn(
          "text-xs font-semibold uppercase tracking-wider",
          hasNonCompliant ? (dark ? "text-amber-300" : "text-amber-700") : dark ? "text-teal-300" : "text-teal-800",
        )}
      >
        {translateUi("Cab pass preview")}
      </p>
      <div className="mt-3 flex flex-col gap-4 w-full">
        {passes.map((p) => {
          const c = colors[p.severity];
          const verifyUrl =
            origin && p.digital && p.verificationToken
              ? buildCabPassVerifyUrl(origin, {
                  companyCode: report.companyCode,
                  employeeId: report.employeeId,
                  passId: p.id,
                  token: p.verificationToken,
                  scanCode: p.scanCode || undefined,
                })
              : undefined;
          return (
            <div
              key={p.id}
              className={cn(
                "rounded-xl border p-3 w-full grid grid-cols-1 gap-4 lg:grid-cols-[22rem_minmax(0,1fr)] lg:items-start",
                dark ? "border-slate-700 bg-slate-900/60" : "border-slate-200 bg-white",
              )}
            >
              <div className="w-full max-w-[22rem] shrink-0 justify-self-start">
                <CabPassScaledCardPreview
                  passId={`qa-cab-${p.id}`}
                  className="!mx-0 w-full"
                  paintKey={`${p.id}-${dark}-${p.verificationToken.length}`}
                >
                  <DrivingCabPassCard
                    passColor={p.color}
                    holderName={report.employeeName}
                    holderTitle={report.employeeRole}
                    holderDept={p.issuedByDept || report.employeeDepot}
                    employeeId={report.employeeId}
                    passNumber={p.passNumber}
                    validFrom={p.validFrom}
                    validTo={p.validTo}
                    validityScope={p.validityScope || undefined}
                    issuedByName={p.issuedBy}
                    issuedByDept={p.issuedByDept}
                    endorsements={p.endorsements}
                    issuerSignatureUrl={p.issuerSignatureUrl || undefined}
                    dateRangeOk={cabPassDateRangeOk(p.validFrom, p.validTo)}
                    companyName={report.companyName ?? undefined}
                    holderPhotoUrl={report.employeePhotoUrl?.trim() || p.imageUrl?.trim() || undefined}
                    verifyUrl={verifyUrl}
                    forcePaperStyle
                    interactive={false}
                  />
                </CabPassScaledCardPreview>
              </div>
              <div className="min-w-0 w-full">
                <div className="flex items-start justify-between gap-2">
                  <p className={cn("text-sm font-semibold", dark ? "text-slate-100" : "text-slate-900")}>{p.label}</p>
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1",
                      c.bg,
                      c.fg,
                      c.ring,
                    )}
                  >
                    {SEVERITY_LABEL[p.severity]}
                  </span>
                </div>
                {p.passNumber ? (
                  <p className={cn("text-xs", dark ? "text-slate-400" : "text-slate-500")}>Pass #{p.passNumber}</p>
                ) : null}
                <p className={cn("text-xs mt-1", dark ? "text-slate-400" : "text-slate-600")}>
                  Valid {formatDateUK(p.validFrom)} –{" "}
                  <span
                    className={
                      p.expired
                        ? dark
                          ? "text-rose-300 font-semibold"
                          : "text-red-700 font-semibold"
                        : cabPassExpiresToday(p.validTo)
                          ? dark
                            ? "text-amber-300 font-semibold"
                            : "text-amber-700 font-semibold"
                          : ""
                    }
                  >
                    {formatDateUK(p.validTo)}
                  </span>
                </p>
                {p.expired ? (
                  <p className={cn("text-xs mt-0.5 font-medium", dark ? "text-rose-300" : "text-red-700")}>
                    Expired on {formatDateUK(p.validTo)}
                  </p>
                ) : cabPassExpiresToday(p.validTo) ? (
                  <p className={cn("text-xs mt-0.5 font-medium", dark ? "text-amber-300" : "text-amber-700")}>
                    Expires on {formatDateUK(p.validTo)}
                  </p>
                ) : null}
                {!p.signed && !p.expired ? (
                  <p className={cn("text-xs mt-0.5 font-medium", dark ? "text-amber-300" : "text-amber-700")}>
                    {translateUi("Issuer signature missing")}
                  </p>
                ) : null}
                {p.issuedBy || p.issuedByDept ? (
                  <p className={cn("text-[11px] mt-1", dark ? "text-slate-500" : "text-slate-500")}>
                    {[p.issuedByDept, p.issuedBy].filter(Boolean).join(" · ")}
                  </p>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CyclesTimelineBlock({ section, dark }: { section: VerificationSection; dark: boolean }) {
  const stats = section.stats || {};
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 min-w-0">
      <StatCard label="Live cycles" value={Number(stats.liveCount ?? 0)} dark={dark} tone={Number(stats.liveCount ?? 0) > 1 ? "warn" : "default"} />
      <StatCard label="Closed cycles" value={Number(stats.closedCount ?? 0)} dark={dark} />
      <StatCard label="Total" value={Number(stats.total ?? 0)} dark={dark} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Print helpers — clone with inlined chart images, native pagination  */
/* ------------------------------------------------------------------ */

/** Fired when the print flow finishes so the live UI can clear `chartRasterFreeze`. */
const QA_VERIFICATION_EXPORT_END_EVENT = "qa-verification-export-end";

/** After `isAnimationActive={false}`, Recharts needs a brief paint before serialising the SVG. */
const QA_PRINT_CHART_SETTLE_MS = 600;
/** Long fallback when the caller does **not** freeze charts first (legacy print path). */
const QA_CHART_ANIMATION_EXPORT_SETTLE_MS = 3800;

/** Page (and inter-pane gutter) colour. Picked so leftover slack reads as gutter, never "white gap". */
function printPageBg(dark: boolean): string {
  return dark ? "#0f172a" : "#f1f5f9";
}

/** Rules applied during print so the cloned report renders correctly across A4 pages. */
const QA_EXPORT_STYLES = `
  *, *::before, *::after {
    box-sizing: border-box;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  html, body {
    margin: 0;
    padding: 0;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  /* Chart hosts: keep visible + sized in both export paths. */
  .qa-chart-host { position: relative !important; overflow: hidden !important; }
  /* Inlined chart images (data URI <img>) — fill host, never overflow. */
  .qa-chart-host img.qa-chart-image {
    display: block !important;
    width: 100% !important;
    height: 100% !important;
    object-fit: contain !important;
    margin: 0 auto !important;
  }
  /* Header logo + employee photo: stable size for both export paths. */
  .qa-export-brand-mark {
    display: block !important;
    height: 176px !important; max-height: 176px !important;
    width: auto !important; max-width: 420px !important;
    object-fit: contain !important; flex-shrink: 0 !important;
  }
  .qa-export-emp-photo-wrap {
    width: 56px !important; height: 56px !important;
    min-width: 56px !important; min-height: 56px !important;
    flex-shrink: 0 !important; overflow: hidden !important;
  }
  .qa-export-emp-photo-wrap img { width: 100% !important; height: 100% !important; object-fit: cover !important; }
  /* The popup's interactive bits never appear in the print clone or the html2canvas slice. */
  #qa-verification-print-root .print\\:hidden { display: none !important; }
  /* Print-only helpers (unused ribbons can opt in). Data protection ribbon is always visible. */
  .qa-print-only { display: none !important; }
  /*
   * Print pagination goals:
   *  - Page 1: header (employee, then full-width outcome) + data-protection ribbon + summary visuals
   *  - Page 2 onwards: section details starting with Main
   *  - Avoid giant blank bottoms (do not blanket break-inside:avoid on tall panes)
   */
  @page { size: A4 portrait; margin: 8mm; }
  @media print {
    html, body {
      height: auto !important;
      overflow: visible !important;
      width: 100% !important;
    }
    body > *:not(#qa-verification-print-root) { display: none !important; }
    #qa-verification-print-root {
      display: block !important;
      position: static !important;
      left: auto !important; top: auto !important;
      width: 100% !important; max-width: 100% !important;
      z-index: auto !important; pointer-events: auto !important;
      margin: 0 auto !important;
      padding: 0 !important;
      box-sizing: border-box !important;
      overflow: visible !important;
    }
    #qa-verification-print-root .qa-print-only {
      display: block !important;
    }
    #qa-verification-print-root .qa-data-protection-ribbon {
      display: block !important;
      break-inside: avoid !important;
      page-break-inside: avoid !important;
      margin: 0.35rem 0 !important;
      padding: 0.45rem 0.7rem !important;
      border-radius: 0.5rem !important;
      border: 1px solid #fecaca !important;
      background: #fef2f2 !important;
      color: #7f1d1d !important;
      box-shadow: inset 4px 0 0 0 #dc2626 !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    #qa-verification-print-root .qa-data-protection-ribbon .qa-dp-title {
      display: block !important;
      font-size: 9px !important;
      font-weight: 700 !important;
      letter-spacing: 0.08em !important;
      text-transform: uppercase !important;
      color: #991b1b !important;
      margin: 0 0 0.2rem 0 !important;
    }
    #qa-verification-print-root .qa-data-protection-ribbon .qa-dp-body {
      margin: 0 !important;
      font-size: 9.5px !important;
      line-height: 1.35 !important;
      color: #7f1d1d !important;
    }
    #qa-verification-print-root .qa-report-document {
      max-width: 100% !important;
      width: 100% !important;
      min-width: 0 !important;
      overflow-x: hidden !important;
      gap: 0.65rem !important;
    }
    #qa-verification-print-root .qa-report-document.space-y-6 > :not([hidden]) ~ :not([hidden]),
    #qa-verification-print-root .qa-report-document.print\\:space-y-5 > :not([hidden]) ~ :not([hidden]) {
      margin-top: 0.65rem !important;
    }
    /* Flow panes by default so leftover page space gets used. */
    #qa-verification-print-root [data-qa-pane="true"] {
      box-shadow: none !important;
      max-width: 100% !important;
      min-width: 0 !important;
      overflow-x: hidden !important;
      break-inside: auto !important;
      page-break-inside: auto !important;
      break-before: auto !important;
      page-break-before: auto !important;
      padding: 0.55rem 0.7rem !important;
    }
    #qa-verification-print-root [data-qa-pane="true"] > header {
      margin-bottom: 0.45rem !important;
      break-inside: avoid !important;
      page-break-inside: avoid !important;
      break-after: avoid-page !important;
      page-break-after: avoid !important;
    }
    /* Header pane: title + employee top-left beside logo; outcome full width below. */
    #qa-verification-print-root [data-qa-header-pane="true"] {
      padding: 0.35rem 0.5rem !important;
    }
    #qa-verification-print-root [data-qa-header-pane="true"] .qa-header-top {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) auto !important;
      column-gap: 0.65rem !important;
      row-gap: 0.45rem !important;
      align-items: start !important;
    }
    #qa-verification-print-root [data-qa-header-pane="true"] .qa-header-copy {
      grid-column: 1 !important;
      grid-row: 1 !important;
      order: 0 !important;
    }
    #qa-verification-print-root [data-qa-header-pane="true"] .qa-export-brand-mark-wrap {
      grid-column: 2 !important;
      grid-row: 1 !important;
      order: 0 !important;
      align-self: start !important;
      justify-self: end !important;
    }
    #qa-verification-print-root [data-qa-header-pane="true"] .qa-header-outcome {
      grid-column: 1 / -1 !important;
      grid-row: 2 !important;
      order: 0 !important;
    }
    #qa-verification-print-root [data-qa-header-pane="true"] .grid.grid-cols-2,
    #qa-verification-print-root [data-qa-header-pane="true"] .grid.sm\\:grid-cols-3,
    #qa-verification-print-root [data-qa-header-pane="true"] .grid.md\\:grid-cols-5 {
      display: grid !important;
      grid-template-columns: repeat(5, minmax(0, 1fr)) !important;
      gap: 0.35rem !important;
      margin-top: 0.4rem !important;
    }
    /* Summary visuals stay with page-1 front matter; keep the two donuts side-by-side. */
    #qa-verification-print-root [data-qa-summary-charts="true"] {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 0.55rem !important;
      max-width: 100% !important;
      min-width: 0 !important;
    }
    #qa-verification-print-root [data-qa-summary-charts="true"] > div {
      max-width: 100% !important;
      min-width: 0 !important;
      overflow: hidden !important;
      padding: 0.45rem !important;
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    #qa-verification-print-root [data-qa-pane="true"] [data-qa-chart-export="summary-overall"],
    #qa-verification-print-root [data-qa-pane="true"] [data-qa-chart-export="summary-sections"] {
      height: 130px !important;
      min-height: 130px !important;
      max-width: 100% !important;
    }
    #qa-verification-print-root [data-qa-pane="true"] [data-qa-summary-charts="true"] > div > ul {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 0.15rem !important;
      margin-top: 0.35rem !important;
      font-size: 9px !important;
      max-width: 100% !important;
      min-width: 0 !important;
    }
    #qa-verification-print-root [data-qa-pane="true"] [data-qa-summary-charts="true"] > div > ul > li {
      min-width: 0 !important;
      overflow: hidden !important;
    }
    /* Section details begin on page 2 (after header + summary visuals). */
    #qa-verification-print-root [data-qa-sections-start="true"] {
      break-before: page !important;
      page-break-before: always !important;
    }
    /* Later sections flow; keep titles with following content. */
    #qa-verification-print-root [data-qa-section-pane="true"] {
      break-inside: auto !important;
      page-break-inside: auto !important;
    }
    #qa-verification-print-root [data-qa-section-pane="true"] > header {
      break-after: avoid-page !important;
      page-break-after: avoid !important;
    }
    /* Closing panes: soft break only — avoid forcing a near-empty sheet. */
    #qa-verification-print-root [data-qa-after-sections="true"] {
      break-before: auto !important;
      page-break-before: auto !important;
    }
    #qa-verification-print-root .qa-chart-host,
    #qa-verification-print-root ul.space-y-2\\.5 > li {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    /* Tighten experience encounters chart so it stays inside its pane. */
    #qa-verification-print-root [data-qa-chart-export="experience-encounters"] {
      height: 200px !important;
      min-height: 200px !important;
    }
    /* Brand mark: large, top-right beside employee details. */
    #qa-verification-print-root .qa-export-brand-mark {
      height: 160px !important; max-height: 160px !important;
      max-width: 380px !important;
    }
  }
`;

/**
 * Recharts often nests several SVGs (defs, axis layers). Pick the one that actually carries
 * paths so live-→-clone mirroring grabs the chart we can see, not an empty layer.
 */
function findMainChartSvg(host: HTMLElement): SVGSVGElement | null {
  const svgs = [...host.querySelectorAll<SVGSVGElement>("svg")];
  if (svgs.length === 0) return null;
  const score = (s: SVGSVGElement) => {
    const r = s.getBoundingClientRect();
    const area = Math.max(0, r.width) * Math.max(0, r.height);
    const inkTags = s.querySelectorAll(
      'path:not([fill="transparent"]):not([fill="none"]), path[fill], circle[fill], rect[fill]',
    ).length;
    const rough = inkTags || s.querySelectorAll("path,g.recharts-sector,circle,line,polygon,polyline,rect").length;
    return area * Math.max(1, rough);
  };
  let best = svgs[0];
  let bestScore = score(best);
  for (let i = 1; i < svgs.length; i++) {
    const sc = score(svgs[i]);
    if (sc > bestScore) {
      bestScore = sc;
      best = svgs[i];
    }
  }
  return best;
}

/**
 * Convert an SVG element into a self-contained `data:image/svg+xml;base64,...` URI. The serialized
 * document carries its own dimensions and namespaces so it renders identically anywhere — print
 * preview, html2canvas, downloaded PDFs.
 */
function svgToDataUri(svg: SVGSVGElement, w: number, h: number): string {
  const fresh = svg.cloneNode(true) as SVGSVGElement;
  if (!fresh.getAttribute("xmlns")) fresh.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  if (!fresh.getAttribute("xmlns:xlink")) fresh.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
  fresh.setAttribute("width", String(w));
  fresh.setAttribute("height", String(h));
  if (!fresh.getAttribute("viewBox")) fresh.setAttribute("viewBox", `0 0 ${w} ${h}`);
  const xml = new XMLSerializer().serializeToString(fresh);
  /** Use unicode-safe base64 so non-ASCII labels (e.g. names with accents) survive serialization. */
  const utf8 = unescape(encodeURIComponent(xml));
  return `data:image/svg+xml;base64,${btoa(utf8)}`;
}

/**
 * Replace each Recharts surface inside `target` with an inlined `<img>` snapshot of the same
 * chart from the live, rendered DOM in `live`. Resolves the long-standing print/PDF problem
 * where Recharts' multi-layer SVGs failed to render after cloneNode/html2canvas.
 *
 * Returns a promise that resolves once every replacement image has decoded.
 */
async function inlineLiveChartsAsImages(live: HTMLElement, target: HTMLElement): Promise<void> {
  const pendingDecodes: Promise<void>[] = [];

  const replaceOne = (liveHost: HTMLElement, tgtHost: HTMLElement | null) => {
    if (!tgtHost) return;
    const liveSvg = findMainChartSvg(liveHost);
    if (!liveSvg) return;
    const svgRect = liveSvg.getBoundingClientRect();
    const hostRect = liveHost.getBoundingClientRect();
    const w = Math.max(1, Math.ceil(svgRect.width || hostRect.width || 400));
    const h = Math.max(1, Math.ceil(svgRect.height || hostRect.height || 200));

    const dataUri = svgToDataUri(liveSvg, w, h);
    const img = document.createElement("img");
    img.className = "qa-chart-image";
    img.alt = "";
    img.decoding = "async";
    img.style.cssText = `display:block;width:100%;height:100%;object-fit:contain;margin:0 auto;`;

    /** Clear the host's chart subtree (responsive container or any svg) and drop the image in. */
    const rc = tgtHost.querySelector<HTMLElement>(".recharts-responsive-container");
    const wrapper = rc ?? tgtHost.querySelector<HTMLElement>(".recharts-wrapper");
    if (wrapper?.parentNode) {
      wrapper.parentNode.replaceChild(img, wrapper);
    } else {
      const oldSvg = tgtHost.querySelector<SVGSVGElement>("svg");
      if (oldSvg?.parentNode) oldSvg.parentNode.replaceChild(img, oldSvg);
      else tgtHost.appendChild(img);
    }
    img.src = dataUri;
    /** decode() avoids racing html2canvas/print preview before the bitmap is ready. */
    pendingDecodes.push(
      img.decode().catch(() => new Promise<void>((res) => {
        if (img.complete) return res();
        img.addEventListener("load", () => res(), { once: true });
        img.addEventListener("error", () => res(), { once: true });
      })),
    );
  };

  live.querySelectorAll<HTMLElement>("[data-qa-chart-export]").forEach((src) => {
    const id = src.getAttribute("data-qa-chart-export");
    if (!id) return;
    const tgt = target.querySelector<HTMLElement>(`[data-qa-chart-export="${id}"]`);
    replaceOne(src, tgt);
  });
  const liveHosts = live.querySelectorAll<HTMLElement>(".qa-chart-host:not([data-qa-chart-export])");
  const tgtHosts = target.querySelectorAll<HTMLElement>(".qa-chart-host:not([data-qa-chart-export])");
  liveHosts.forEach((src, i) => replaceOne(src, tgtHosts[i] ?? null));

  await Promise.all(pendingDecodes);
}

/** Build a fully-prepared, off-screen clone of the report ready for either print or PDF capture. */
async function buildExportClone(
  liveRoot: HTMLElement,
  dark: boolean,
  opts?: { id?: string },
): Promise<{ wrapper: HTMLElement; clone: HTMLElement; cleanup: () => void }> {
  const wrapper = document.createElement("div");
  wrapper.id = opts?.id ?? "qa-verification-export-root";
  wrapper.setAttribute("aria-hidden", "true");
  wrapper.style.cssText = [
    "position:fixed",
    "left:-99999px",
    "top:0",
    /* Match A4 content width (~194mm at 10mm margins) so print layout does not clip. */
    "width:190mm",
    "max-width:100%",
    "padding:2mm 3mm",
    "box-sizing:border-box",
    "pointer-events:none",
    "z-index:-1",
    `background:${printPageBg(dark)}`,
  ].join(";") + ";";

  const clone = liveRoot.cloneNode(true) as HTMLElement;
  clone.querySelectorAll<HTMLElement>(".print\\:hidden").forEach((el) => el.remove());
  wrapper.appendChild(clone);
  document.body.appendChild(wrapper);

  /** Two animation frames + a tiny pause give the cloned tree a layout before chart inlining. */
  await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
  await new Promise<void>((r) => setTimeout(r, 60));

  await inlineLiveChartsAsImages(liveRoot, clone);

  /** Make sure decoded images influence layout before consumer reads bounding rects. */
  await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
  await new Promise<void>((r) => setTimeout(r, 40));

  return {
    wrapper,
    clone,
    cleanup: () => {
      wrapper.remove();
    },
  };
}

/**
 * Print: build the off-screen clone (with inlined chart images) and call `window.print()`. The
 * browser's own dialog can save as PDF too, so we don't need a separate jsPDF/html2canvas path.
 * Page 1 packs header + summary visuals; section details start on page 2 and flow without
 * forcing near-empty sheets.
 */
function printNode(
  liveRoot: HTMLElement,
  title: string,
  dark: boolean,
  opts?: { scheduleDelayMs?: number },
): void {
  let wrapper: HTMLElement | null = null;
  let scheduleTimer: number | undefined;
  let fallbackTimer: number | undefined;

  const sessionStyle = document.createElement("style");
  sessionStyle.setAttribute("data-qa-print-session", "true");
  const screenBg = printPageBg(dark);
  sessionStyle.textContent = `${QA_EXPORT_STYLES}
@media screen {
  #qa-verification-print-root {
    position: fixed !important;
    left: -99999px !important;
    top: 0 !important;
    width: min(190mm, 100%) !important;
    pointer-events: none !important;
    opacity: 1 !important;
    z-index: -1 !important;
    background: ${screenBg} !important;
  }
}
@media print {
  html, body { background: ${screenBg} !important; }
  #qa-verification-print-root { background: ${screenBg} !important; }
}`;

  const prevTitle = document.title;
  document.title = title.replace(/[<>]/g, "");
  document.head.appendChild(sessionStyle);

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    document.title = prevTitle;
    sessionStyle.remove();
    wrapper?.remove();
    wrapper = null;
    window.removeEventListener("afterprint", onAfterPrint);
    if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer);
    if (scheduleTimer !== undefined) window.clearTimeout(scheduleTimer);
    window.dispatchEvent(new CustomEvent(QA_VERIFICATION_EXPORT_END_EVENT, { detail: { source: "print" as const } }));
  };
  const onAfterPrint = () => {
    if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer);
    cleanup();
  };

  const begin = async () => {
    try {
      const built = await buildExportClone(liveRoot, dark, { id: "qa-verification-print-root" });
      wrapper = built.wrapper;
      window.addEventListener("afterprint", onAfterPrint);
      fallbackTimer = window.setTimeout(cleanup, 45_000);
      /** One more rAF gives the print stylesheet a chance to paint before the dialog opens. */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.setTimeout(() => window.print(), 100);
        });
      });
    } catch (e) {
      console.error("Print preparation failed:", e);
      cleanup();
    }
  };

  scheduleTimer = window.setTimeout(() => {
    void begin();
  }, opts?.scheduleDelayMs ?? QA_CHART_ANIMATION_EXPORT_SETTLE_MS);
}

/* ------------------------------------------------------------------ */
/* Top-level report view                                               */
/* ------------------------------------------------------------------ */

const ALL_VERIFICATION_SEVERITIES: VerificationSeverity[] = ["C", "A", "R", "NA"];

function defaultSeverityFilterState(): Record<VerificationSeverity, boolean> {
  return { C: true, A: true, R: true, NA: true };
}

function severityFilterIsActive(filters: Record<VerificationSeverity, boolean>): boolean {
  return ALL_VERIFICATION_SEVERITIES.some((sev) => !filters[sev]);
}

function severityFilterLabel(filters: Record<VerificationSeverity, boolean>): string {
  const active = ALL_VERIFICATION_SEVERITIES.filter((sev) => filters[sev]);
  if (active.length === ALL_VERIFICATION_SEVERITIES.length) return "All outcomes";
  if (active.length === 0) return "None selected";
  return active.map((sev) => SEVERITY_LABEL[sev]).join(", ");
}

/** Apply outcome filters for on-screen view and print (same DOM subtree). */
function buildFilteredVerificationReport(
  report: VerificationReport,
  filters: Record<VerificationSeverity, boolean>,
): VerificationReport {
  const active = ALL_VERIFICATION_SEVERITIES.filter((sev) => filters[sev]);
  if (active.length === ALL_VERIFICATION_SEVERITIES.length) return report;
  const activeSet = new Set(active);

  const sections = report.sections
    .map((sec) => ({
      ...sec,
      items: sec.items.filter((it) => activeSet.has(it.severity)),
    }))
    .filter((sec) => sec.items.length > 0);

  const nonCompliance = report.nonCompliance.filter((it) => activeSet.has(it.severity));

  const counts: VerificationCounts = { C: 0, A: 0, R: 0, NA: 0 };
  for (const sec of sections) {
    for (const it of sec.items) counts[it.severity] += 1;
  }

  return { ...report, sections, nonCompliance, counts };
}

type SeverityFilterPreset = "all" | "issues" | "review";

function severityFiltersForPreset(preset: SeverityFilterPreset): Record<VerificationSeverity, boolean> {
  switch (preset) {
    case "issues":
      return { C: false, A: true, R: true, NA: false };
    case "review":
      return { C: false, A: false, R: true, NA: false };
    default:
      return defaultSeverityFilterState();
  }
}

export function VerificationReportView({
  report,
  isDarkMode,
  onClose,
  onNavigateToFix,
}: {
  report: VerificationReport;
  isDarkMode?: boolean;
  onClose?: () => void;
  /** Opens the employee profile tab where this advisory/review item can be resolved. */
  onNavigateToFix?: (target: VerificationFixTarget) => void;
}) {
  const dark = !!isDarkMode;
  const reportRef = useRef<HTMLDivElement>(null);
  const fullTotal =
    report.counts.C + report.counts.A + report.counts.R + report.counts.NA;

  const [severityFilters, setSeverityFilters] = useState(defaultSeverityFilterState);
  const filtersActive = severityFilterIsActive(severityFilters);
  const filteredReport = useMemo(
    () => buildFilteredVerificationReport(report, severityFilters),
    [report, severityFilters],
  );

  const verdict = overallVerdict(report.overall);
  const counts = filteredReport.counts;
  const total = counts.C + counts.A + counts.R + counts.NA;
  const visibleSeverities = ALL_VERIFICATION_SEVERITIES.filter((sev) => severityFilters[sev]);

  const derogations = report.derogations ?? [];
  const derogationCount = derogations.length;

  const summaryPie = useMemo(
    () =>
      visibleSeverities
        .map((sev) => ({
          name: SEVERITY_LABEL[sev],
          value: counts[sev],
          sev,
        }))
        .filter((entry) => entry.value > 0 || !filtersActive),
    [counts, visibleSeverities, filtersActive],
  );

  const summarySectionPie = useMemo(
    () =>
      filteredReport.sections
        .map((sec, index) => ({
          name: translateUi(sec.label),
          value: sec.items.length,
          sectionId: sec.id,
          color: sectionChartHex(sec.id, index),
        }))
        .filter((entry) => entry.value > 0),
    [filteredReport.sections],
  );

  const sectionChecksTotal = useMemo(
    () => summarySectionPie.reduce((sum, entry) => sum + entry.value, 0),
    [summarySectionPie],
  );

  const dataProtectionNotice = useMemo(() => buildDataProtectionPrintNotice(), []);

  const [exportError, setExportError] = useState<string | null>(null);
  /** Off during print export so Recharts draws final geometry before the SVG is serialised. */
  const [chartRasterFreeze, setChartRasterFreeze] = useState(false);
  const chartAnimate = !chartRasterFreeze;

  useEffect(() => {
    const onEnd = (ev: Event) => {
      const ce = ev as CustomEvent<{ source?: string }>;
      if (ce.detail?.source === "print") setChartRasterFreeze(false);
    };
    window.addEventListener(QA_VERIFICATION_EXPORT_END_EVENT, onEnd);
    return () => window.removeEventListener(QA_VERIFICATION_EXPORT_END_EVENT, onEnd);
  }, []);

  const toggleSeverity = useCallback((sev: VerificationSeverity) => {
    setSeverityFilters((prev) => {
      const next = { ...prev, [sev]: !prev[sev] };
      if (!ALL_VERIFICATION_SEVERITIES.some((s) => next[s])) return prev;
      return next;
    });
  }, []);

  const applyPreset = useCallback((preset: SeverityFilterPreset) => {
    setSeverityFilters(severityFiltersForPreset(preset));
  }, []);

  const canPrint = visibleSeverities.length > 0 && total > 0;

  const handlePrint = useCallback(() => {
    if (!reportRef.current || !canPrint) return;
    setExportError(null);
    setChartRasterFreeze(true);
    window.setTimeout(() => {
      try {
        const suffix = filtersActive ? ` (${severityFilterLabel(severityFilters)})` : "";
        printNode(reportRef.current!, `Verification report — ${report.employeeName}${suffix}`, dark, {
          scheduleDelayMs: QA_PRINT_CHART_SETTLE_MS + 150,
        });
      } catch (e) {
        setChartRasterFreeze(false);
        setExportError(e instanceof Error ? e.message : translateUi("Print failed"));
      }
    }, 200);
  }, [report.employeeName, dark, canPrint, filtersActive, severityFilters]);

  const colors = dark ? SEVERITY_COLORS_DARK : SEVERITY_COLORS;
  const tooltipStyle = dark
    ? { background: "#0f172a", border: "1px solid #334155", color: "#e2e8f0" }
    : undefined;

  return (
    <div className={cn("space-y-6 min-w-0 max-w-full overflow-x-hidden", dark ? "text-slate-100" : "text-slate-900")}>
      {/* Toolbar */}
      <div className={cn("flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3 print:hidden min-w-0")}>
        <div className="min-w-0">
          <h2 className={cn("text-xl sm:text-2xl font-bold tracking-tight break-words", dark ? "text-slate-100" : "text-slate-900")}>
            {translateUi("Verification report")}
          </h2>
          <p className={cn("text-sm break-words", dark ? "text-slate-400" : "text-slate-500")}>
            {report.employeeName} · {report.employeeRole} · {report.employeeDepot}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 shrink-0 w-full sm:w-auto">
          <button
            type="button"
            onClick={handlePrint}
            disabled={!canPrint}
            className={cn(
              "px-4 py-2.5 sm:py-2 rounded-xl text-sm font-medium transition-colors w-full sm:w-auto",
              canPrint
                ? "bg-indigo-600 text-white hover:bg-indigo-700"
                : "bg-slate-300 text-slate-500 cursor-not-allowed",
            )}
          >
            {translateUi("Print / Save as PDF")}
          </button>
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className={cn(
                "px-4 py-2.5 sm:py-2 rounded-xl text-sm font-medium w-full sm:w-auto",
                dark ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100",
              )}
            > {translateUi("Close")} </button>
          ) : null}
        </div>
      </div>

      {/* Outcome filters — screen only; filtered content inside reportRef is what prints */}
      <div
        className={cn(
          "rounded-2xl border p-3 sm:p-4 space-y-3 print:hidden min-w-0",
          dark ? "bg-slate-900/80 border-slate-700" : "bg-white border-slate-200 shadow-sm",
        )}
      >
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start sm:justify-between gap-3">
          <div className="min-w-0">
            <p className={cn("text-sm font-semibold", dark ? "text-slate-100" : "text-slate-900")}>
              {translateUi("Filter outcomes for view & print")}
            </p>
            <p className={cn("text-xs mt-0.5 break-words", dark ? "text-slate-400" : "text-slate-500")}>
              {filtersActive
                ? `Showing ${total} of ${fullTotal} checks · ${severityFilterLabel(severityFilters)}`
                : "All checks included — use filters before printing to include only what you need."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0 w-full sm:w-auto">
            {(
              [
                ["all", translateUi("All")],
                ["issues", "Issues only"],
                ["review", "Review only"],
              ] as const
            ).map(([preset, label]) => (
              <button
                key={preset}
                type="button"
                onClick={() => applyPreset(preset)}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors whitespace-nowrap",
                  (preset === "all" && !filtersActive) ||
                    (preset === "issues" &&
                      severityFilters.A &&
                      severityFilters.R &&
                      !severityFilters.C &&
                      !severityFilters.NA) ||
                    (preset === "review" &&
                      severityFilters.R &&
                      !severityFilters.C &&
                      !severityFilters.A &&
                      !severityFilters.NA)
                    ? dark
                      ? "bg-indigo-500/20 border-indigo-400/50 text-indigo-200"
                      : "bg-indigo-50 border-indigo-200 text-indigo-800"
                    : dark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-200 text-slate-600 hover:bg-slate-50",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="-mx-0.5 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:thin] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:pb-0">
          {ALL_VERIFICATION_SEVERITIES.map((sev) => {
            const on = severityFilters[sev];
            const c = colors[sev];
            return (
              <button
                key={sev}
                type="button"
                onClick={() => toggleSeverity(sev)}
                className={cn(
                  "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold ring-1 transition-all whitespace-nowrap shrink-0",
                  on ? cn(c.bg, c.fg, c.ring) : dark ? "bg-slate-800/60 text-slate-500 ring-slate-700" : "bg-slate-50 text-slate-400 ring-slate-200",
                )}
                aria-pressed={on}
              >
                <span
                  className={cn(
                    "inline-flex h-4 w-4 items-center justify-center rounded border text-[10px]",
                    on ? "border-current" : dark ? "border-slate-600" : "border-slate-300",
                  )}
                >
                  {on ? "✓" : ""}
                </span>
                {SEVERITY_LABEL[sev]}
                <span className="tabular-nums opacity-80">({report.counts[sev]})</span>
              </button>
            );
          })}
        </div>
        {!canPrint ? (
          <p className={cn("text-xs", dark ? "text-amber-300" : "text-amber-700")}>
            {translateUi("Select at least one outcome with matching checks to print.")}
          </p>
        ) : null}
      </div>

      {exportError ? (
        <div
          className={cn(
            "rounded-xl text-sm p-3 print:hidden",
            dark ? "bg-rose-500/10 text-rose-300 border border-rose-500/30" : "bg-red-50 text-red-700 border border-red-100",
          )}
        >
          {exportError}
        </div>
      ) : null}

      <div
        ref={reportRef}
        className={cn(
          "qa-report-document space-y-6 print:space-y-5 min-w-0",
          dark ? "rv-dark text-slate-100" : "text-slate-900",
        )}
      >
        {filtersActive ? (
          <div
            className={cn(
              "rounded-xl border px-4 py-3 text-sm",
              dark ? "bg-indigo-500/10 border-indigo-500/30 text-indigo-100" : "bg-indigo-50 border-indigo-100 text-indigo-900",
            )}
          >
            <span className="font-semibold">{translateUi("Filtered report.")}</span>{" "}
            {total} of {fullTotal} checks · {severityFilterLabel(severityFilters)} · overall run outcome:{" "}
            {verdict.label}
          </div>
        ) : null}

        {/* Header pane — title + employee beside logo; outcome full width below */}
        <Pane
          title={translateUi("Compliance verification report")}
          description={`Run ${formatDateTime(report.runAt)}`}
          dark={dark}
          headerPane
          rightSlot={(
            <img
              src={dark ? "/rail-intel-logo-dark.png?v=18" : "/rail-intel-logo-light.png?v=18"}
              alt={translateUi("Rail Intel")}
              className="qa-export-brand-mark h-16 w-auto max-h-16 max-w-[min(100%,12rem)] object-contain shrink-0 sm:h-28 sm:max-h-28 sm:max-w-[min(100%,18rem)] md:h-40 md:max-h-40 md:max-w-[min(100%,22rem)] lg:h-44 lg:max-h-44"
              crossOrigin="anonymous"
            />
          )}
          lead={(
            <div className="flex items-start gap-3 sm:gap-3.5 min-w-0 w-full">
              <div
                className={cn(
                  "qa-export-emp-photo-wrap h-12 w-12 sm:h-14 sm:w-14 rounded-2xl ring-1 overflow-hidden bg-slate-100 shrink-0",
                  dark ? "ring-slate-700" : "ring-slate-200",
                )}
              >
                {report.employeePhotoUrl ? (
                  <img src={report.employeePhotoUrl} alt={report.employeeName} className="h-full w-full object-cover" />
                ) : (
                  <div className={cn("h-full w-full flex items-center justify-center text-base font-semibold", dark ? "text-slate-300" : "text-slate-500")}>
                    {report.employeeName ? report.employeeName.charAt(0).toUpperCase() : "?"}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1 overflow-hidden">
                <p className={cn("text-[11px] uppercase tracking-wider font-semibold leading-none", dark ? "text-slate-400" : "text-slate-500")}> {translateUi("Employee")} </p>
                <p className={cn("text-lg sm:text-xl font-bold break-words [overflow-wrap:anywhere] leading-tight mt-1", dark ? "text-slate-100" : "text-slate-900")}>{report.employeeName}</p>
                <p className={cn("text-sm mt-0.5 break-words [overflow-wrap:anywhere] leading-snug", dark ? "text-slate-400" : "text-slate-600")}>
                  {report.employeeRole} · {report.employeeDepot}
                  {report.employeePayNo ? ` · Pay no. ${report.employeePayNo}` : ""}
                </p>
                {report.companyName ? (
                  <p className={cn("text-xs mt-0.5 break-words [overflow-wrap:anywhere] leading-snug", dark ? "text-slate-500" : "text-slate-500")}>
                    {report.companyName} ({report.companyCode})
                  </p>
                ) : (
                  <p className={cn("text-xs mt-0.5 leading-snug", dark ? "text-slate-500" : "text-slate-500")}>{report.companyCode}</p>
                )}
                {(report.runByName || "").trim() ? (
                  <p className={cn("text-xs mt-0.5 break-words [overflow-wrap:anywhere] leading-snug", dark ? "text-slate-500" : "text-slate-500")}>
                    Verification conducted by: {(report.runByName || "").trim()}
                  </p>
                ) : null}
              </div>
            </div>
          )}
        >
          <div
            className={cn(
              "rounded-2xl p-3 sm:p-3.5 md:p-4 ring-2 min-w-0 w-full max-w-full overflow-hidden",
              dark ? "bg-slate-800/60" : "bg-slate-50",
            )}
            style={{ boxShadow: `inset 0 0 0 2px ${verdict.color}33` }}
          >
            <p className={cn("text-xs uppercase tracking-wider font-semibold", dark ? "text-slate-400" : "text-slate-500")}> {translateUi("Outcome")} </p>
            <p
              className="text-xl sm:text-2xl font-bold mt-1.5 whitespace-normal break-words [overflow-wrap:anywhere] leading-snug"
              style={{ color: verdict.color }}
            >
              {verdict.label}
            </p>
            <div className="mt-3 grid grid-cols-2 min-[420px]:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-2.5 text-center min-w-0 w-full">
              {visibleSeverities.map((sev) => (
                <div
                  key={sev}
                  className={cn(
                    "rounded-xl min-w-0 px-1.5 py-2.5 sm:px-2 sm:py-3",
                    colors[sev].bg,
                    sev === "R" && counts.R > 0 && "qa-review-attention-pulse",
                  )}
                >
                  <p className={cn("text-lg sm:text-xl font-bold tabular-nums", colors[sev].fg)}>{counts[sev]}</p>
                  <p
                    className={cn(
                      "mt-1 text-[10px] sm:text-[11px] font-semibold uppercase tracking-normal leading-tight break-words [overflow-wrap:anywhere]",
                      colors[sev].fg,
                    )}
                  >
                    {SEVERITY_LABEL[sev]}
                  </p>
                </div>
              ))}
              <div
                className={cn(
                  "rounded-xl min-w-0 px-1.5 py-2.5 sm:px-2 sm:py-3",
                  dark ? "bg-violet-500/15" : "bg-violet-50",
                )}
              >
                <p className={cn("text-lg sm:text-xl font-bold tabular-nums", dark ? "text-violet-300" : "text-violet-700")}>
                  {derogationCount}
                </p>
                <p
                  className={cn(
                    "mt-1 text-[10px] sm:text-[11px] font-semibold uppercase tracking-normal leading-tight break-words [overflow-wrap:anywhere]",
                    dark ? "text-violet-300" : "text-violet-700",
                  )}
                > {translateUi("Derogations")} </p>
              </div>
            </div>
          </div>
        </Pane>

        {/* Data protection ribbon — shown on screen and in print/PDF */}
        <aside
          className={cn(
            "qa-data-protection-ribbon rounded-xl border px-3.5 py-2.5 sm:px-4 sm:py-3",
            dark
              ? "border-red-500/40 bg-red-500/10 text-red-100 shadow-[inset_4px_0_0_0_#f87171]"
              : "border-red-200 bg-red-50 text-red-900 shadow-[inset_4px_0_0_0_#dc2626]",
          )}
          data-qa-print-ribbon="data-protection"
          role="note"
          aria-label={dataProtectionNotice.title}
        >
          <span
            className={cn(
              "qa-dp-title block text-[10px] font-bold uppercase tracking-wider mb-1",
              dark ? "text-red-300" : "text-red-800",
            )}
          >
            {dataProtectionNotice.title}
          </span>
          <p
            className={cn(
              "qa-dp-body m-0 text-xs sm:text-[13px] leading-snug",
              dark ? "text-red-100/95" : "text-red-900/90",
            )}
          >
            {dataProtectionNotice.body}
          </p>
        </aside>

        {/* Summary visuals pane */}
        <Pane title={translateUi("Summary visuals")} description={`${total} checks across ${filteredReport.sections.length} sections`} dark={dark}>
          <div data-qa-summary-charts="true" className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 min-w-0">
            <div className={cn("rounded-2xl ring-1 p-3 relative min-w-0 overflow-hidden", dark ? "ring-slate-700" : "ring-slate-200")}>
              <p className={cn("text-xs font-semibold uppercase tracking-wider", dark ? "text-slate-400" : "text-slate-500")}>
                {translateUi("Overall outcome")}
              </p>
              <div className="qa-chart-host relative mt-2 h-56 min-h-[14rem]" data-qa-chart-export="summary-overall">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={summaryPie} dataKey="value" nameKey="name" innerRadius={62} outerRadius={86} isAnimationActive={chartAnimate} paddingAngle={summaryPie.length > 1 ? 2 : 0}>
                      {summaryPie.map((entry) => (
                        <Cell key={entry.sev} fill={colors[entry.sev].chartHex} stroke={dark ? "#0f172a" : "#ffffff"} strokeWidth={2} />
                      ))}
                      <Label
                        content={renderDonutHoleLabel(
                          total,
                          "checks",
                          dark ? "#f1f5f9" : "#0f172a",
                          dark ? "#94a3b8" : "#64748b",
                        )}
                      />
                    </Pie>
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value: number, name: string) => {
                        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
                        return [`${value} checks (${pct}%)`, name];
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5 min-w-0">
                {summaryPie.map((entry) => {
                  const pct = total > 0 ? (entry.value / total) * 100 : 0;
                  const pctLabel = total > 0 ? pct.toFixed(1) : "0.0";
                  return (
                    <li key={entry.sev} className="flex items-center justify-between gap-1.5 text-[11px] leading-tight min-w-0 overflow-hidden">
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span
                          className="inline-block h-2 w-2 shrink-0 rounded-full"
                          style={{ background: colors[entry.sev].chartHex }}
                        />
                        <span className={cn("font-medium truncate", dark ? "text-slate-300" : "text-slate-600")}>
                          {entry.name}
                        </span>
                      </span>
                      <span className="shrink-0 text-right tabular-nums whitespace-nowrap">
                        <span className={cn("font-semibold", dark ? "text-slate-100" : "text-slate-900")}>
                          {entry.value}
                        </span>
                        <span className={cn("ml-1", dark ? "text-slate-500" : "text-slate-500")}>({pctLabel}%)</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className={cn("rounded-2xl ring-1 p-3 relative min-w-0 overflow-hidden", dark ? "ring-slate-700" : "ring-slate-200")}>
              <p className={cn("text-xs font-semibold uppercase tracking-wider", dark ? "text-slate-400" : "text-slate-500")}>
                {translateUi("Checks by section")}
              </p>
              <div className="qa-chart-host relative mt-2 h-56 min-h-[14rem]" data-qa-chart-export="summary-sections">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={summarySectionPie}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={62}
                      outerRadius={86}
                      isAnimationActive={chartAnimate}
                      paddingAngle={summarySectionPie.length > 1 ? 2 : 0}
                    >
                      {summarySectionPie.map((entry) => (
                        <Cell key={entry.sectionId} fill={entry.color} stroke={dark ? "#0f172a" : "#ffffff"} strokeWidth={2} />
                      ))}
                      <Label
                        content={renderDonutHoleLabel(
                          summarySectionPie.length,
                          "sections",
                          dark ? "#f1f5f9" : "#0f172a",
                          dark ? "#94a3b8" : "#64748b",
                        )}
                      />
                    </Pie>
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value: number, name: string) => {
                        const pct = sectionChecksTotal > 0 ? ((value / sectionChecksTotal) * 100).toFixed(1) : "0.0";
                        return [`${value} check${value === 1 ? "" : "s"} (${pct}%)`, name];
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {summarySectionPie.length === 0 ? (
                <p className={cn("mt-3 text-sm italic", dark ? "text-slate-400" : "text-slate-500")}>
                  {translateUi("No section checks match the current filters.")}
                </p>
              ) : (
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5 min-w-0">
                  {summarySectionPie.map((entry) => {
                    const pct = sectionChecksTotal > 0 ? (entry.value / sectionChecksTotal) * 100 : 0;
                    const pctLabel = sectionChecksTotal > 0 ? pct.toFixed(1) : "0.0";
                    return (
                      <li key={entry.sectionId} className="flex items-center justify-between gap-1.5 text-[11px] leading-tight min-w-0 overflow-hidden">
                        <span className="flex items-center gap-1.5 min-w-0">
                          <span
                            className="inline-block h-2 w-2 shrink-0 rounded-full"
                            style={{ background: entry.color }}
                          />
                          <span className={cn("font-medium truncate", dark ? "text-slate-300" : "text-slate-600")}>
                            {entry.name}
                          </span>
                        </span>
                        <span className="shrink-0 text-right tabular-nums whitespace-nowrap">
                          <span className={cn("font-semibold", dark ? "text-slate-100" : "text-slate-900")}>
                            {entry.value}
                          </span>
                          <span className={cn("ml-1", dark ? "text-slate-500" : "text-slate-500")}>({pctLabel}%)</span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </Pane>

        {/* Per-section panes */}
        {filteredReport.sections.length === 0 ? (
          <Pane title={translateUi("Section details")} dark={dark} description="No checks match the current filters">
            <p className={cn("text-sm", dark ? "text-slate-400" : "text-slate-500")}>
              {translateUi("Adjust the outcome filters above to include compliant, advisory, review, or N/A results.")}
            </p>
          </Pane>
        ) : (
          filteredReport.sections.map((sec, sectionIndex) => {
          const isExperience = sec.id === "experience";
          const isAssessmentEvents = sec.id === "assessment_events";
          const isCycles = sec.id === "cycles";
          const isCabPasses = sec.id === "cab_passes";
          return (
            <Pane key={sec.id} title={translateUi(sec.label)} dark={dark}
              description={`${sec.items.length} check${sec.items.length === 1 ? "" : "s"}`}
              sectionPane
              sectionsStart={sectionIndex === 0}
            >
              <div className="space-y-5">
                {isExperience && sec.stats ? (
                  <ExperienceStatsBlock stats={sec.stats} dark={dark} chartAnimate={chartAnimate} />
                ) : null}
                {isAssessmentEvents && sec.stats ? (
                  <AssessmentEventsStatsBlock stats={sec.stats} dark={dark} chartAnimate={chartAnimate} />
                ) : null}
                {isCycles ? <CyclesTimelineBlock section={sec} dark={dark} /> : null}
                {isCabPasses && sec.stats ? <CabPassPreviewBlock stats={sec.stats} dark={dark} report={report} /> : null}
                <ItemList items={sec.items} dark={dark} onNavigateToFix={onNavigateToFix} />
              </div>
            </Pane>
          );
        })
        )}

        {/* Non-compliance summary pane */}
        <Pane
          title={translateUi("Summary of non-compliance")}
          description={
            filteredReport.nonCompliance.length === 0
              ? filtersActive
                ? translateUi("No items match the current outcome filters.")
                : translateUi("All sections passed without advisories.")
              : `${filteredReport.nonCompliance.length} item${filteredReport.nonCompliance.length === 1 ? "" : "s"} require attention`
          }
          dark={dark}
          afterSections
        >
          {filteredReport.nonCompliance.length === 0 ? (
            <div className={cn("rounded-2xl p-6 text-center font-semibold", colors.C.bg, colors.C.fg)}>
              {filtersActive ? translateUi("No non-compliance items match the selected outcomes.") : translateUi("No non-compliance items raised.")}
            </div>
          ) : (
            <ul className="space-y-2.5">
              {filteredReport.nonCompliance.map((it) => {
                const c = colors[it.severity];
                return (
                  <li
                    key={`nc-${it.id}`}
                    className={cn(
                      "rounded-xl border p-3.5 ring-1 ring-inset flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3",
                      c.bg,
                      c.ring,
                      dark ? "border-slate-700/60" : "border-slate-200",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <p className={cn("text-xs uppercase tracking-wider", dark ? "text-slate-400" : "text-slate-500")}>
                        {translateUi((filteredReport.sections.find((s) => s.id === it.section)?.label) || it.section)}
                      </p>
                      <p className={cn("text-sm font-semibold mt-0.5 break-words", dark ? "text-slate-100" : "text-slate-900")}>{it.title}</p>
                      <p className={cn("text-sm mt-0.5 break-words", dark ? "text-slate-300" : "text-slate-700")}>{it.detail}</p>
                      {onNavigateToFix ? (
                        <div className="mt-3 print:hidden">
                          <VerificationFixButton item={it} dark={dark} onNavigateToFix={onNavigateToFix} />
                        </div>
                      ) : null}
                    </div>
                    <div className="flex flex-col items-start sm:items-end gap-2 shrink-0">
                      {severityChip(it.severity, dark)}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Pane>

        {/* Derogations pane — assessor seniority derogations recorded when starting assessments. */}
        <Pane
          title={translateUi("Derogations")}
          description={
            derogationCount === 0
              ? "No assessment derogations recorded."
              : `${derogationCount} derogation${derogationCount === 1 ? "" : "s"} recorded`
          }
          dark={dark}
          rightSlot={
            derogationCount > 0 ? (
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1",
                  dark ? "bg-violet-500/15 text-violet-300 ring-violet-500/30" : "bg-violet-50 text-violet-700 ring-violet-200",
                )}
              >
                {derogationCount}
              </span>
            ) : undefined
          }
        >
          {derogationCount === 0 ? (
            <div className={cn("rounded-2xl p-6 text-center font-semibold", colors.C.bg, colors.C.fg)}>
              {translateUi("No assessment derogations were recorded.")}
            </div>
          ) : (
            <ul className="space-y-2.5">
              {derogations.map((d) => {
                const junior = d.relationship === "assessor_junior";
                const lateAssessment = d.relationship === "late_assessment";
                const relationshipLabel = lateAssessment
                  ? "Late assessment derogation"
                  : junior
                    ? translateUi("Assessor below required seniority")
                    : "Assessor in same seniority tier";
                return (
                  <li
                    key={`derog-${d.id}`}
                    className={cn(
                      "rounded-xl border p-3.5 ring-1 ring-inset",
                      dark ? "bg-violet-500/10 border-slate-700/60 ring-violet-500/30" : "bg-violet-50 border-slate-200 ring-violet-200",
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className={cn("text-xs uppercase tracking-wider", dark ? "text-slate-400" : "text-slate-500")}>
                          {relationshipLabel}
                          {d.event_name ? ` — ${d.event_name}` : ""}
                        </p>
                        {lateAssessment ? (
                          <p className={cn("text-sm font-semibold mt-0.5", dark ? "text-slate-100" : "text-slate-900")}>
                            {d.assessor_name || d.assessor_email || "Assessor"}
                            {d.assessor_role ? ` (${d.assessor_role})` : ""}
                          </p>
                        ) : (
                          <p className={cn("text-sm font-semibold mt-0.5", dark ? "text-slate-100" : "text-slate-900")}>
                            {d.assessor_name || d.assessor_email || "Assessor"}
                            {d.assessor_role ? ` (${d.assessor_role}${d.assessor_tier_title ? `, ${d.assessor_tier_title}` : ""})` : ""}
                            {" assessing "}
                            {d.assessee_role || "employee"}
                            {d.assessee_tier_title ? ` (${d.assessee_tier_title})` : ""}
                          </p>
                        )}
                        <p className={cn("text-sm mt-1", dark ? "text-slate-300" : "text-slate-700")}>
                          <span className="font-medium">{translateUi("Reason:")}</span> {d.reason}
                        </p>
                        <p className={cn("text-sm mt-0.5", dark ? "text-slate-300" : "text-slate-700")}>
                          <span className="font-medium">{translateUi("Approved by:")}</span> {d.approved_by}
                        </p>
                        <p className={cn("text-xs mt-1.5", dark ? "text-slate-400" : "text-slate-500")}>
                          Recorded by {d.filled_in_by_name || d.filled_in_by_email || "unknown"} on {formatDateTime(d.created_at)}
                        </p>
                      </div>
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 shrink-0",
                          dark ? "bg-violet-500/15 text-violet-300 ring-violet-500/30" : "bg-violet-100 text-violet-700 ring-violet-200",
                        )}
                      > {translateUi("Derogation")} </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Pane>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Modal that wraps the scan + report flow (used by employee profile) */
/* ------------------------------------------------------------------ */

export function VerificationFlowModal({
  open,
  employeeId,
  employeeName,
  companyCode,
  currentUserEmail,
  currentUserName,
  isDarkMode,
  onClose,
  onNavigateToFix,
}: {
  open: boolean;
  employeeId: string;
  employeeName: string;
  companyCode: string | undefined;
  currentUserEmail: string | undefined;
  currentUserName?: string | undefined;
  isDarkMode?: boolean;
  onClose: () => void;
  onNavigateToFix?: (target: VerificationFixTarget) => void;
}) {
  const dark = !!isDarkMode;
  const { progress, report, restart, cancel } = useVerificationRun({
    employeeId,
    companyCode,
    currentUserEmail,
    currentUserName,
    enabled: open,
  });

  useEffect(() => {
    if (!open) cancel();
  }, [open, cancel]);

  if (!open) return null;
  return (
    <div className={cn("fixed inset-0 z-[60] overflow-y-auto overflow-x-hidden", dark ? "bg-slate-950/80" : "bg-slate-900/60", "backdrop-blur-sm")}>
      <div className="min-h-full flex items-start justify-center p-2 sm:p-4 lg:p-8">
        <div className="w-full max-w-6xl min-w-0">
          {progress.status !== "ready" || !report ? (
            <div className="flex justify-center py-8">
              <VerificationScanModal progress={progress} isDarkMode={dark} employeeName={employeeName} />
            </div>
          ) : (
            <div
              className={cn(
                "rounded-2xl sm:rounded-3xl shadow-2xl border p-3 sm:p-5 lg:p-8 min-w-0 max-w-full overflow-x-hidden",
                dark ? "bg-slate-900 border-slate-700" : "bg-slate-50 border-slate-200",
              )}
            >
              <VerificationReportView
                report={report}
                isDarkMode={dark}
                onClose={onClose}
                onNavigateToFix={onNavigateToFix}
              />
            </div>
          )}
          {progress.status === "error" ? (
            <div className="flex justify-center mt-4 gap-2">
              <button
                type="button"
                onClick={restart}
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
              > {translateUi("Retry")} </button>
              <button
                type="button"
                onClick={onClose}
                className={cn(
                  "px-4 py-2 rounded-xl text-sm font-medium",
                  dark ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100",
                )}
              > {translateUi("Close")} </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Dashboard: run QA for all assessing-manager reportees, then show team reports. */
export function TeamReporteeVerificationModal({
  open,
  companyCode,
  currentUserEmail,
  currentUserName,
  reporteeCount,
  isDarkMode,
  onClose,
  onComplete,
  onNavigateToFix,
}: {
  open: boolean;
  companyCode?: string;
  currentUserEmail?: string;
  currentUserName?: string;
  reporteeCount: number;
  isDarkMode?: boolean;
  onClose: () => void;
  /** Called after a successful team run so the dashboard glance can refresh. */
  onComplete?: () => void;
  onNavigateToFix?: (target: VerificationFixTarget & { employeeId: string }) => void;
}) {
  const dark = !!isDarkMode;
  const colors = dark ? SEVERITY_COLORS_DARK : SEVERITY_COLORS;
  const [phase, setPhase] = useState<"confirm" | "running" | "ready" | "error">("confirm");
  const [error, setError] = useState<string | null>(null);
  const [sampled, setSampled] = useState<BulkSampledEmployee[]>([]);
  const [reportsByEmployee, setReportsByEmployee] = useState<Record<string, VerificationReport>>({});
  const [completedRuns, setCompletedRuns] = useState(0);
  const [failedRuns, setFailedRuns] = useState(0);

  useEffect(() => {
    if (!open) {
      setPhase("confirm");
      setError(null);
      setSampled([]);
      setReportsByEmployee({});
      setCompletedRuns(0);
      setFailedRuns(0);
    }
  }, [open]);

  const runTeam = useCallback(async () => {
    if (!companyCode || phase === "running") return;
    setPhase("running");
    setError(null);
    setSampled([]);
    setReportsByEmployee({});
    try {
      const res = await fetch(
        `/api/companies/${encodeURIComponent(companyCode)}/verification/runs/bulk?companyCode=${encodeURIComponent(companyCode)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Company-Code": companyCode,
            ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
            ...((currentUserName || "").trim() && !(currentUserName || "").includes("@")
              ? { "X-User-Name": (currentUserName || "").trim() }
              : {}),
          },
          body: JSON.stringify({ scope: "reportees" }),
        },
      );
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        completedRuns?: number;
        failedRuns?: number;
        sampled?: BulkSampledEmployee[];
        reports?: VerificationReport[];
      };
      if (!res.ok) throw new Error(data.error || `Team run failed (${res.status})`);
      const nextSampled = Array.isArray(data.sampled) ? data.sampled : [];
      setSampled(nextSampled);
      setCompletedRuns(Number(data.completedRuns ?? 0));
      setFailedRuns(Number(data.failedRuns ?? 0));
      const map: Record<string, VerificationReport> = {};
      for (const report of Array.isArray(data.reports) ? data.reports : []) {
        const empId = String(report?.employeeId ?? "").trim();
        if (empId) map[empId] = report;
      }
      setReportsByEmployee(map);
      setPhase("ready");
      onComplete?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : translateUi("Team verification failed"));
      setPhase("error");
    }
  }, [companyCode, currentUserEmail, currentUserName, onComplete, phase]);

  const aggregate = useMemo(() => {
    const completed = sampled.filter((x) => x.status === "completed");
    const totals = completed.reduce(
      (acc, x) => {
        acc.C += Number(x.counts?.C ?? 0);
        acc.A += Number(x.counts?.A ?? 0);
        acc.R += Number(x.counts?.R ?? 0);
        acc.NA += Number(x.counts?.NA ?? 0);
        return acc;
      },
      { C: 0, A: 0, R: 0, NA: 0 },
    );
    const totalChecks = totals.C + totals.A + totals.R + totals.NA;
    const complianceRate = totalChecks > 0 ? (totals.C / totalChecks) * 100 : 0;
    const outcomes = { pass: 0, advisory: 0, fail: 0 };
    for (const row of completed) {
      if (row.overall === "pass") outcomes.pass += 1;
      else if (row.overall === "advisory") outcomes.advisory += 1;
      else outcomes.fail += 1;
    }
    return {
      totals,
      totalChecks,
      complianceRate,
      outcomes,
      donutData: [
        { name: "Compliant", value: totals.C, sev: "C" as const },
        { name: "Advisory", value: totals.A, sev: "A" as const },
        { name: "Review", value: totals.R, sev: "R" as const },
        { name: "N/A", value: totals.NA, sev: "NA" as const },
      ],
    };
  }, [sampled]);

  /** Every completed reportee report, worst outcome first, so the manager sees the whole team in one view. */
  const orderedReports = useMemo(() => {
    const severityRank: Record<string, number> = { fail: 0, advisory: 1, pass: 2 };
    return sampled
      .filter((s) => s.status === "completed" && reportsByEmployee[s.employeeId])
      .map((s) => ({ sampled: s, report: reportsByEmployee[s.employeeId] }))
      .sort((a, b) => {
        const ra = severityRank[a.report.overall] ?? 3;
        const rb = severityRank[b.report.overall] ?? 3;
        if (ra !== rb) return ra - rb;
        return (a.report.employeeName || "").localeCompare(b.report.employeeName || "");
      });
  }, [sampled, reportsByEmployee]);

  if (!open) return null;

  return (
    <div className={cn("fixed inset-0 z-[70] overflow-y-auto overflow-x-hidden", dark ? "bg-slate-950/80" : "bg-slate-900/60", "backdrop-blur-sm")}>
      <div className="min-h-full flex items-start justify-center p-2 sm:p-4 lg:p-8">
        <div className="w-full max-w-6xl min-w-0">
          {phase === "confirm" || phase === "error" ? (
            <div
              className={cn(
                "rounded-2xl sm:rounded-3xl shadow-2xl border p-3 sm:p-5 lg:p-8 min-w-0 max-w-full overflow-x-hidden",
                dark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200",
              )}
            >
              <h2 className={cn("text-xl font-bold", dark ? "text-slate-50" : "text-slate-900")}>
                {translateUi("Run QA for all reportees")}
              </h2>
              <p className={cn("text-sm mt-2 leading-relaxed", dark ? "text-slate-300" : "text-slate-600")}>
                This runs a full employee verification for each person who lists you as their assessing manager
                {reporteeCount > 0 ? ` (${reporteeCount} reportee${reporteeCount === 1 ? "" : "s"})` : ""}. Results are
                saved to QA history and a team report opens when the run finishes.
              </p>
              {error ? (
                <p className={cn("mt-4 text-sm font-medium", dark ? "text-rose-300" : "text-rose-700")} role="alert">
                  {error}
                </p>
              ) : null}
              <div className="mt-6 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void runTeam()}
                  disabled={!companyCode || reporteeCount <= 0}
                  className="px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
                >
                  {phase === "error" ? translateUi("Retry team run") : translateUi("Start team run")}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className={cn(
                    "px-4 py-2.5 rounded-xl text-sm font-semibold",
                    dark ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100",
                  )}
                > {translateUi("Cancel")} </button>
              </div>
            </div>
          ) : null}

          {phase === "running" ? (
            <div className="flex justify-center py-8">
              <div
                className={cn(
                  "w-full max-w-lg rounded-3xl border shadow-2xl p-8 text-center",
                  dark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200",
                )}
              >
                <div
                  className={cn(
                    "mx-auto h-12 w-12 animate-spin rounded-full border-b-2",
                    dark ? "border-indigo-400" : "border-indigo-600",
                  )}
                />
                <h3 className={cn("mt-5 text-lg font-bold", dark ? "text-slate-50" : "text-slate-900")}>
                  {translateUi("Verifying your reportees…")}
                </h3>
                <p className={cn("mt-2 text-sm", dark ? "text-slate-400" : "text-slate-500")}>
                  Running checks for {reporteeCount} employee{reporteeCount === 1 ? "" : "s"}. This can take a little
                  while — keep this window open.
                </p>
              </div>
            </div>
          ) : null}

          {phase === "ready" ? (
            <div
              className={cn(
                "rounded-2xl sm:rounded-3xl shadow-2xl border p-3 sm:p-5 lg:p-7 space-y-5 min-w-0 max-w-full overflow-x-hidden",
                dark ? "bg-slate-900 border-slate-700" : "bg-slate-50 border-slate-200",
              )}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className={cn("text-xl font-bold", dark ? "text-slate-50" : "text-slate-900")}>
                    {translateUi("Team QA report")}
                  </h2>
                  <p className={cn("text-sm mt-1", dark ? "text-slate-400" : "text-slate-500")}>
                    Your reportees only · {completedRuns} completed
                    {failedRuns > 0 ? ` · ${failedRuns} failed` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className={cn(
                    "px-3 py-2 rounded-xl text-sm font-semibold",
                    dark ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-200/80",
                  )}
                > {translateUi("Close")} </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[16rem_minmax(0,1fr)] gap-4">
                <div className={cn("rounded-2xl ring-1 p-3", dark ? "ring-slate-700 bg-slate-950/40" : "ring-slate-200 bg-white")}>
                  <p className={cn("text-xs font-semibold uppercase tracking-wider", dark ? "text-slate-400" : "text-slate-500")}>
                    {translateUi("Team compliance")}
                  </p>
                  <div className="relative mt-2 h-48">
                    <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 text-center">
                      <p className={cn("text-2xl font-bold tabular-nums leading-none", dark ? "text-slate-100" : "text-slate-900")}>
                        {aggregate.complianceRate.toFixed(1)}%
                      </p>
                      <p className={cn("mt-1 text-[10px] font-semibold uppercase tracking-wider", dark ? "text-slate-500" : "text-slate-500")}>
                        compliant
                      </p>
                    </div>
                    {aggregate.totalChecks > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={aggregate.donutData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={72} isAnimationActive>
                            {aggregate.donutData.map((entry) => (
                              <Cell key={entry.sev} fill={colors[entry.sev].chartHex} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={dark ? { background: "#0f172a", border: "1px solid #334155", color: "#e2e8f0" } : undefined}
                            formatter={(value: number, name: string) => [`${value} checks`, name]}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className={cn("h-full flex items-center justify-center text-xs px-4 text-center", dark ? "text-slate-400" : "text-slate-500")}>
                        {translateUi("No completed checks in this run.")}
                      </div>
                    )}
                  </div>
                  <div className={cn("mt-1 flex flex-wrap gap-2 text-[11px] font-semibold", dark ? "text-slate-400" : "text-slate-500")}>
                    <span>Pass {aggregate.outcomes.pass}</span>
                    <span aria-hidden>·</span>
                    <span className={dark ? "text-amber-300" : "text-amber-700"}>Advisory {aggregate.outcomes.advisory}</span>
                    <span aria-hidden>·</span>
                    <span className={dark ? "text-rose-300" : "text-rose-700"}>Review {aggregate.outcomes.fail}</span>
                  </div>
                </div>

                <div className={cn("rounded-2xl ring-1 p-3", dark ? "ring-slate-700 bg-slate-950/40" : "ring-slate-200 bg-white")}>
                  <p className={cn("text-xs font-semibold uppercase tracking-wider mb-2", dark ? "text-slate-400" : "text-slate-500")}>
                    {translateUi("Reportees in this run · jump to a report")}
                  </p>
                  <div className="flex flex-wrap gap-2 max-h-56 overflow-y-auto">
                    {sampled.map((emp) => {
                      const hasReport = emp.status === "completed" && !!reportsByEmployee[emp.employeeId];
                      const outcomeColor =
                        emp.status !== "completed"
                          ? dark
                            ? "bg-slate-800 ring-slate-600 text-slate-300"
                            : "bg-slate-100 ring-slate-200 text-slate-600"
                          : emp.overall === "pass"
                            ? dark
                              ? "bg-emerald-500/10 ring-emerald-500/30 text-emerald-300"
                              : "bg-emerald-50 ring-emerald-200 text-emerald-700"
                            : emp.overall === "advisory"
                              ? dark
                                ? "bg-amber-500/10 ring-amber-500/30 text-amber-300"
                                : "bg-amber-50 ring-amber-200 text-amber-700"
                              : dark
                                ? "bg-rose-500/10 ring-rose-500/30 text-rose-300"
                                : "bg-rose-50 ring-rose-200 text-rose-700";
                      return (
                        <button
                          key={`${emp.employeeId}-${emp.runId ?? "failed"}`}
                          type="button"
                          disabled={!hasReport}
                          onClick={() => {
                            const node = document.getElementById(`team-qa-report-${emp.employeeId}`);
                            node?.scrollIntoView({ behavior: "smooth", block: "start" });
                          }}
                          className={cn(
                            "rounded-xl px-3 py-2 ring-1 min-w-0 sm:min-w-[10rem] max-w-full text-left transition",
                            outcomeColor,
                            hasReport ? "hover:opacity-90" : "opacity-70 cursor-not-allowed",
                          )}
                        >
                          <p className="text-xs font-semibold truncate">{emp.employeeName || emp.employeeId}</p>
                          <p className="text-[11px] mt-0.5">
                            {emp.status === "completed"
                              ? `Compliance ${emp.complianceRate.toFixed(1)}%`
                              : `Failed${emp.error ? `: ${emp.error}` : ""}`}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {orderedReports.length > 0 ? (
                <div className="space-y-4">
                  {orderedReports.map(({ report }) => (
                    <div
                      key={String(report.employeeId)}
                      id={`team-qa-report-${report.employeeId}`}
                      className={cn(
                        "rounded-3xl border p-4 sm:p-6 scroll-mt-4",
                        dark ? "bg-slate-950/50 border-slate-700" : "bg-white border-slate-200",
                      )}
                    >
                      <VerificationReportView
                        report={report}
                        isDarkMode={dark}
                        onNavigateToFix={
                          onNavigateToFix
                            ? (target) => {
                                onNavigateToFix({
                                  ...target,
                                  employeeId: String(report.employeeId || ""),
                                });
                                onClose();
                              }
                            : undefined
                        }
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <p className={cn("text-sm", dark ? "text-slate-400" : "text-slate-500")}>
                  {translateUi("No completed reportee reports in this run.")}
                </p>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* QA Verifications page (company-wide history)                        */
/* ------------------------------------------------------------------ */

type RunRow = {
  runId: string;
  employeeId: string;
  employeeName: string;
  employeeRole?: string;
  employeeDepot?: string;
  runAt: string;
  runByEmail: string | null;
  runByName: string | null;
  overall: string;
  counts: VerificationCounts;
  nonComplianceCount: number;
};

type BulkSampledEmployee = {
  employeeId: string;
  employeeName: string;
  runId: string | null;
  status: "completed" | "failed";
  overall: "pass" | "advisory" | "fail";
  complianceRate: number;
  counts: VerificationCounts;
  error?: string;
};

type BulkRunResult = {
  percentage: number;
  totalEmployees: number;
  selectedEmployees: number;
  completedRuns: number;
  failedRuns: number;
  sampled: BulkSampledEmployee[];
};

/** Keep only the most recent verification run per employee (for aggregate compliance tiles). */
function latestRunPerEmployee<T extends { employeeId: string; runAt: string }>(runs: T[]): T[] {
  const byEmployee = new Map<string, T>();
  for (const run of runs) {
    const existing = byEmployee.get(run.employeeId);
    if (!existing || String(run.runAt).localeCompare(String(existing.runAt)) > 0) {
      byEmployee.set(run.employeeId, run);
    }
  }
  return Array.from(byEmployee.values());
}

const SEVERITY_PRINT_HEX: Record<VerificationSeverity, string> = {
  C: "#10b981",
  A: "#f59e0b",
  R: "#ef4444",
  NA: "#94a3b8",
};

function printHtmlDocument(html: string): void {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;";
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;
  const doc = frameWindow?.document;
  if (!doc) {
    iframe.remove();
    return;
  }

  let printed = false;
  const cleanup = () => {
    window.setTimeout(() => iframe.remove(), 500);
  };
  const triggerPrint = () => {
    if (printed) return;
    printed = true;
    try {
      frameWindow?.focus();
      frameWindow?.print();
    } finally {
      cleanup();
    }
  };

  doc.open();
  doc.write(html);
  doc.close();

  iframe.addEventListener("load", triggerPrint, { once: true });
  window.setTimeout(triggerPrint, 400);
}

const QA_PRINT_BASE_STYLES = `
  body { font-family: system-ui, sans-serif; color: #0f172a; margin: 24px; }
  h1 { font-size: 1.25rem; margin: 0 0 4px; }
  h2 { font-size: 1rem; margin: 0 0 12px; }
  h3 { font-size: 0.95rem; margin: 0 0 4px; }
  .meta { color: #64748b; font-size: 0.85rem; margin-bottom: 16px; }
  .total { display: inline-block; padding: 8px 12px; border-radius: 8px; font-weight: 700; margin-bottom: 16px; }
  table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
  th, td { border-bottom: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; vertical-align: top; }
  th { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.04em; color: #64748b; }
  .items-section { margin-top: 24px; }
  .employee-block { margin-top: 16px; padding-top: 12px; border-top: 1px solid #e2e8f0; }
  .employee-block:first-of-type { margin-top: 0; padding-top: 0; border-top: 0; }
  .employee-meta { color: #64748b; font-size: 0.8rem; margin: 0 0 12px; }
  .item { border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; margin-bottom: 10px; break-inside: avoid; page-break-inside: avoid; }
  .item-section { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.04em; color: #64748b; margin: 0 0 4px; }
  .item-title { font-size: 0.9rem; font-weight: 600; margin: 0 0 4px; }
  .item-detail { font-size: 0.85rem; margin: 0; color: #334155; }
  .item-refs { font-size: 0.75rem; margin: 6px 0 0; color: #64748b; }
  .derogation-card { border: 1px solid #ddd6fe; border-radius: 8px; padding: 10px 12px; margin-bottom: 10px; background: #f5f3ff; break-inside: avoid; page-break-inside: avoid; }
  .derogation-kind { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.04em; color: #64748b; margin: 0 0 4px; }
  .derogation-heading { font-size: 0.9rem; font-weight: 600; margin: 0 0 6px; }
  .derogation-line { font-size: 0.85rem; margin: 0 0 4px; color: #334155; }
  .derogation-meta { font-size: 0.75rem; margin: 6px 0 0; color: #64748b; }
  @media print { body { margin: 12mm; } }
`;

/** RTDB often stores arrays as numeric-key objects; normalise before iterating. */
function rtdbAsArray<T>(value: unknown): T[] {
  if (!value) return [];
  if (Array.isArray(value)) return value as T[];
  if (typeof value === "object") {
    return Object.entries(value as Record<string, T>)
      .filter(([key]) => /^\d+$/.test(key))
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([, entry]) => entry);
  }
  return [];
}

function normalizeVerificationSeverity(raw: unknown): VerificationSeverity | null {
  const value = String(raw ?? "").trim().toUpperCase();
  if (value === "C" || value === "COMPLIANT") return "C";
  if (value === "A" || value === "ADVISORY") return "A";
  if (value === "R" || value === "REVIEW") return "R";
  if (value === "NA" || value === "N/A") return "NA";
  return null;
}

function normalizeVerificationItem(raw: unknown): VerificationItem | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const severity = normalizeVerificationSeverity(row.severity);
  if (!severity) return null;
  const references = rtdbAsArray<VerificationItemReference>(row.references).filter(
    (ref) => ref && typeof ref === "object",
  );
  return {
    id: String(row.id ?? ""),
    section: String(row.section ?? ""),
    title: String(row.title ?? ""),
    severity,
    detail: String(row.detail ?? ""),
    references: references.length > 0 ? references : undefined,
  };
}

function normalizeVerificationReportForPrint(report: VerificationReport): VerificationReport {
  const sections = rtdbAsArray<VerificationSection>(report.sections).map((sec) => ({
    ...sec,
    items: rtdbAsArray<unknown>(sec.items)
      .map((item) => normalizeVerificationItem(item))
      .filter((item): item is VerificationItem => item !== null),
  }));
  const nonCompliance = rtdbAsArray<unknown>(report.nonCompliance)
    .map((item) => normalizeVerificationItem(item))
    .filter((item): item is VerificationItem => item !== null);
  return { ...report, sections, nonCompliance };
}

function sectionLabelForVerificationItem(report: VerificationReport, item: VerificationItem): string {
  const section = rtdbAsArray<VerificationSection>(report.sections).find((sec) => sec.id === item.section);
  return translateUi(section?.label || VERIFICATION_SECTION_LABELS[item.section] || item.section || "Section");
}

function collectVerificationItemsBySeverity(
  report: VerificationReport,
  sev: VerificationSeverity,
): { sectionLabel: string; item: VerificationItem }[] {
  const normalized = normalizeVerificationReportForPrint(report);
  const out: { sectionLabel: string; item: VerificationItem }[] = [];
  const seen = new Set<string>();

  const pushItem = (item: VerificationItem, sectionLabel?: string) => {
    if (item.severity !== sev) return;
    const key = item.id || `${item.section}|${item.title}|${item.detail}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      sectionLabel: sectionLabel || sectionLabelForVerificationItem(normalized, item),
      item,
    });
  };

  for (const sec of normalized.sections) {
    for (const item of sec.items) {
      pushItem(item, translateUi(sec.label || VERIFICATION_SECTION_LABELS[sec.id] || sec.id));
    }
  }

  /** nonCompliance is the authoritative flat list for review/advisory outcomes. */
  for (const item of normalized.nonCompliance) pushItem(item);

  return out;
}

async function fetchVerificationReportForPrint(
  companyCode: string,
  row: RunRow,
  currentUserEmail?: string,
): Promise<VerificationReport> {
  const res = await fetch(
    `/api/employees/${encodeURIComponent(row.employeeId)}/verification/runs/${encodeURIComponent(row.runId)}?companyCode=${encodeURIComponent(companyCode)}`,
    {
      headers: {
        "X-Company-Code": companyCode,
        ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
      },
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Failed to load report (${res.status})`);
  return normalizeVerificationReportForPrint(data.report as VerificationReport);
}

function renderVerificationItemPrintHtml(
  sectionLabel: string,
  item: VerificationItem,
): string {
  const refs =
    item.references && item.references.length > 0
      ? `<p class="item-refs">${escapeHtml(item.references.map((r) => `${r.kind}: ${r.label}`).join(" · "))}</p>`
      : "";
  return `<div class="item">
    <p class="item-section">${escapeHtml(sectionLabel)}</p>
    <p class="item-title">${escapeHtml(item.title)}</p>
    <p class="item-detail">${escapeHtml(item.detail)}</p>
    ${refs}
  </div>`;
}

function derogationRelationshipLabel(d: DerogationRecord): string {
  if (d.relationship === "late_assessment") return "Late assessment derogation";
  if (d.relationship === "assessor_junior") return "Assessor below required seniority";
  return "Assessor in same seniority tier";
}

function renderDerogationHeadingHtml(d: DerogationRecord): string {
  const lateAssessment = d.relationship === "late_assessment";
  if (lateAssessment) {
    const assessor = d.assessor_name || d.assessor_email || "Assessor";
    const role = d.assessor_role ? ` (${escapeHtml(d.assessor_role)})` : "";
    return `${escapeHtml(assessor)}${role}`;
  }
  const assessor = d.assessor_name || d.assessor_email || "Assessor";
  const assessorRole = d.assessor_role
    ? ` (${escapeHtml(d.assessor_role)}${d.assessor_tier_title ? `, ${escapeHtml(d.assessor_tier_title)}` : ""})`
    : "";
  const assessee = d.assessee_role || "employee";
  const assesseeTier = d.assessee_tier_title ? ` (${escapeHtml(d.assessee_tier_title)})` : "";
  return `${escapeHtml(assessor)}${assessorRole} assessing ${escapeHtml(assessee)}${assesseeTier}`;
}

function renderDerogationCardPrintHtml(d: DerogationRecord): string {
  const kind = derogationRelationshipLabel(d);
  const eventSuffix = d.event_name ? ` — ${escapeHtml(d.event_name)}` : "";
  const recordedBy = d.filled_in_by_name || d.filled_in_by_email || "unknown";
  return `<div class="derogation-card">
    <p class="derogation-kind">${escapeHtml(kind)}${eventSuffix}</p>
    <p class="derogation-heading">${renderDerogationHeadingHtml(d)}</p>
    <p class="derogation-line"><strong>Reason:</strong> ${escapeHtml(d.reason)}</p>
    <p class="derogation-line"><strong>Approved by:</strong> ${escapeHtml(d.approved_by)}</p>
    <p class="derogation-meta">Recorded by ${escapeHtml(recordedBy)} on ${escapeHtml(formatDateTime(d.created_at))}</p>
  </div>`;
}

async function printComplianceSeveritySummary(
  sev: VerificationSeverity,
  runs: RunRow[],
  opts?: { companyCode?: string; currentUserEmail?: string },
): Promise<void> {
  const deduped = latestRunPerEmployee(runs)
    .filter((row) => Number(row.counts?.[sev] ?? 0) > 0)
    .sort((a, b) => a.employeeName.localeCompare(b.employeeName));
  const total = deduped.reduce((sum, row) => sum + Number(row.counts?.[sev] ?? 0), 0);

  const reportsByRunId = new Map<string, VerificationReport>();
  if (opts?.companyCode) {
    const fetched = await Promise.all(
      deduped.map(async (row) => {
        try {
          const report = await fetchVerificationReportForPrint(opts.companyCode!, row, opts.currentUserEmail);
          return { runId: row.runId, report };
        } catch (err) {
          console.error(`Failed to load verification report for ${row.employeeName}:`, err);
          return null;
        }
      }),
    );
    for (const entry of fetched) {
      if (entry) reportsByRunId.set(entry.runId, entry.report);
    }
  }

  const rowsHtml = deduped
    .map((row) => {
      const count = Number(row.counts?.[sev] ?? 0);
      const verdict = overallVerdict(row.overall as VerificationOverall);
      return `<tr>
        <td>${escapeHtml(row.employeeName || row.employeeId)}</td>
        <td>${escapeHtml([row.employeeRole, row.employeeDepot].filter(Boolean).join(" · "))}</td>
        <td>${escapeHtml(formatDateTime(row.runAt))}</td>
        <td style="color:${verdict.color};font-weight:600;">${escapeHtml(verdict.label)}</td>
        <td style="text-align:right;font-weight:700;color:${SEVERITY_PRINT_HEX[sev]};">${count}</td>
      </tr>`;
    })
    .join("");

  const itemsHtml = deduped
    .map((row) => {
      const report = reportsByRunId.get(row.runId);
      const items = report ? collectVerificationItemsBySeverity(report, sev) : [];
      if (items.length === 0) return "";
      const verdict = overallVerdict(row.overall as VerificationOverall);
      const itemBlocks = items
        .map(({ sectionLabel, item }) => renderVerificationItemPrintHtml(sectionLabel, item))
        .join("");
      return `<section class="employee-block">
        <h3>${escapeHtml(row.employeeName || row.employeeId)}</h3>
        <p class="employee-meta">${escapeHtml([row.employeeRole, row.employeeDepot].filter(Boolean).join(" · "))} · Run ${escapeHtml(formatDateTime(row.runAt))} · <span style="color:${verdict.color};font-weight:600;">${escapeHtml(verdict.label)}</span></p>
        ${itemBlocks}
      </section>`;
    })
    .join("");

  const itemsSectionHtml = itemsHtml
    ? `<div class="items-section"><h2>${escapeHtml(SEVERITY_LABEL[sev])} items</h2>${itemsHtml}</div>`
    : total > 0
      ? `<div class="items-section"><h2>${escapeHtml(SEVERITY_LABEL[sev])} items</h2><p class="meta">Could not load item details for this print. Open the employee report to view individual ${escapeHtml(SEVERITY_LABEL[sev].toLowerCase())} checks.</p></div>`
      : "";

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8" />
<title>QA compliance — ${escapeHtml(SEVERITY_LABEL[sev])}</title>
<style>
  ${QA_PRINT_BASE_STYLES}
  .total { background: ${SEVERITY_PRINT_HEX[sev]}18; color: ${SEVERITY_PRINT_HEX[sev]}; }
</style></head><body>
<h1>${escapeHtml(SEVERITY_LABEL[sev])} (${sev}) — overall compliance</h1>
<p class="meta">${opts?.companyCode ? `Company ${escapeHtml(opts.companyCode)} · ` : ""}Latest run per employee · printed ${escapeHtml(formatDateTime(new Date().toISOString()))}</p>
<p class="total">Total ${escapeHtml(SEVERITY_LABEL[sev])}: ${total}</p>
<h2> Summary </h2>
<table><thead><tr><th> Employee </th><th>Role / depot</th><th>Run at</th><th> Outcome </th><th>${sev}</th></tr></thead>
<tbody>${rowsHtml || `<tr><td colspan="5">No runs in the current list.</td></tr>`}</tbody></table>
${itemsSectionHtml}
</body></html>`;
  printHtmlDocument(html);
}

const DEROGATION_PRINT_HEX = "#8b5cf6";

function printDerogationsSummary(
  derogations: DerogationRecord[],
  employeeNames: Map<string, string>,
  opts?: { companyCode?: string },
) {
  const sorted = [...derogations].sort((a, b) => {
    const nameA = employeeNames.get(a.employee_id) || a.employee_id;
    const nameB = employeeNames.get(b.employee_id) || b.employee_id;
    const byName = nameA.localeCompare(nameB);
    if (byName !== 0) return byName;
    return String(b.created_at).localeCompare(String(a.created_at));
  });

  const rowsHtml = sorted
    .map((d) => {
      const employeeName = employeeNames.get(d.employee_id) || d.employee_id;
      return `<tr>
        <td>${escapeHtml(employeeName)}</td>
        <td>${escapeHtml(d.assessor_name || d.assessor_email || "Assessor")}</td>
        <td>${escapeHtml(derogationRelationshipLabel(d))}</td>
        <td>${escapeHtml(d.event_name || "—")}</td>
        <td>${escapeHtml(formatDateTime(d.created_at))}</td>
        <td>${escapeHtml(d.approved_by)}</td>
      </tr>`;
    })
    .join("");

  const grouped = new Map<string, DerogationRecord[]>();
  for (const d of sorted) {
    const employeeId = String(d.employee_id ?? "").trim();
    const bucket = grouped.get(employeeId) ?? [];
    bucket.push(d);
    grouped.set(employeeId, bucket);
  }

  const detailsHtml = Array.from(grouped.entries())
    .map(([employeeId, records]) => {
      const employeeName = employeeNames.get(employeeId) || employeeId;
      const cards = records.map((d) => renderDerogationCardPrintHtml(d)).join("");
      return `<section class="employee-block">
        <h3>${escapeHtml(employeeName)}</h3>
        <p class="employee-meta">${records.length} derogation${records.length === 1 ? "" : "s"}</p>
        ${cards}
      </section>`;
    })
    .join("");

  const detailsSectionHtml = detailsHtml
    ? `<div class="items-section"><h2>Derogation records</h2>${detailsHtml}</div>`
    : "";

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8" />
<title>QA compliance — Derogations</title>
<style>
  ${QA_PRINT_BASE_STYLES}
  .total { background: ${DEROGATION_PRINT_HEX}18; color: ${DEROGATION_PRINT_HEX}; }
</style></head><body>
<h1>Assessment derogations</h1>
<p class="meta">${opts?.companyCode ? `Company ${escapeHtml(opts.companyCode)} · ` : ""}printed ${escapeHtml(formatDateTime(new Date().toISOString()))}</p>
<p class="total">Total derogations: ${derogations.length}</p>
<h2> Summary </h2>
<table><thead><tr><th> Employee </th><th> Assessor </th><th>Relationship</th><th> Event </th><th>Recorded</th><th>Approved by</th></tr></thead>
<tbody>${rowsHtml || `<tr><td colspan="6">No derogations in the current list.</td></tr>`}</tbody></table>
${detailsSectionHtml}
</body></html>`;
  printHtmlDocument(html);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function SeverityLegendKey({
  dark,
  layout = "inline",
}: {
  dark: boolean;
  layout?: "inline" | "stacked";
}) {
  const colors = dark ? SEVERITY_COLORS_DARK : SEVERITY_COLORS;
  return (
    <div className={cn("flex flex-wrap gap-1.5 normal-case tracking-normal font-medium", layout === "stacked" && "mt-1.5")}>
      {(["C", "A", "R", "NA"] as VerificationSeverity[]).map((sev) => (
        <span
          key={sev}
          className={cn(
            "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] ring-1",
            colors[sev].bg,
            colors[sev].fg,
            colors[sev].ring,
          )}
          title={SEVERITY_LABEL[sev]}
        >
          <span
            className="h-2 w-2 rounded-full shrink-0 ring-1 ring-black/10"
            style={{ backgroundColor: colors[sev].chartHex }}
            aria-hidden
          />
          <span className="font-bold">{sev}</span>
          <span className="opacity-90">{SEVERITY_LABEL[sev]}</span>
        </span>
      ))}
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] ring-1",
          dark ? "bg-violet-500/15 text-violet-300 ring-violet-500/30" : "bg-violet-50 text-violet-700 ring-violet-200",
        )}
        title={translateUi("Assessment derogations (seniority and late assessment)")}
      >
        <span
          className="h-2 w-2 rounded-full shrink-0 ring-1 ring-black/10"
          style={{ backgroundColor: DEROGATION_PRINT_HEX }}
          aria-hidden
        />
        <span className="font-bold">D</span>
        <span className="opacity-90"> Derogations </span>
      </span>
    </div>
  );
}

function ComplianceSectionBreakdownChart({
  bars,
  dark,
  loading,
  employeeCount,
}: {
  bars: SectionBreakdownBar[];
  dark: boolean;
  loading: boolean;
  employeeCount: number;
}) {
  const colors = dark ? SEVERITY_COLORS_DARK : SEVERITY_COLORS;
  const tooltipStyle = dark
    ? { background: "#0f172a", border: "1px solid #334155", color: "#e2e8f0" }
    : undefined;
  const axisColor = dark ? "#94a3b8" : "#64748b";

  return (
    <div
      className={cn(
        "mt-5 pt-4 border-t",
        dark ? "border-slate-700" : "border-slate-200",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <p className={cn("text-xs font-semibold uppercase tracking-wider", dark ? "text-slate-400" : "text-slate-500")}>
            Section breakdown
          </p>
          <p className={cn("text-xs mt-0.5", dark ? "text-slate-500" : "text-slate-500")}>
            Stacked by outcome across latest run{employeeCount === 1 ? "" : "s"} ({employeeCount} employee{employeeCount === 1 ? "" : "s"})
          </p>
        </div>
        <ul className="flex flex-wrap gap-2 justify-end">
          {(["C", "A", "R", "NA"] as VerificationSeverity[]).map((sev) => (
            <li key={sev} className="flex items-center gap-1.5 text-[11px] leading-none">
              <span className="inline-block h-2 w-2 rounded-full" style={{ background: colors[sev].chartHex }} />
              <span className={cn("font-medium", dark ? "text-slate-300" : "text-slate-600")}>{SEVERITY_LABEL[sev]}</span>
            </li>
          ))}
        </ul>
      </div>
      {loading ? (
        <p className={cn("text-sm py-8 text-center", dark ? "text-slate-400" : "text-slate-500")}>Loading section breakdown…</p>
      ) : bars.length === 0 ? (
        <p className={cn("text-sm py-8 text-center italic", dark ? "text-slate-400" : "text-slate-500")}>
          No section data available for the current view.
        </p>
      ) : (
        <div className="h-64 sm:h-72 w-full min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={bars} margin={{ top: 8, right: 12, left: 0, bottom: 48 }}>
              <XAxis
                dataKey="name"
                stroke={axisColor}
                tick={{ fontSize: 10, fill: axisColor }}
                interval={0}
                angle={-28}
                textAnchor="end"
                height={56}
              />
              <YAxis stroke={axisColor} allowDecimals={false} tick={{ fontSize: 11, fill: axisColor }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="C" name={SEVERITY_LABEL.C} stackId="sections" fill={colors.C.chartHex} radius={[0, 0, 0, 0]} />
              <Bar dataKey="A" name={SEVERITY_LABEL.A} stackId="sections" fill={colors.A.chartHex} />
              <Bar dataKey="R" name={SEVERITY_LABEL.R} stackId="sections" fill={colors.R.chartHex} />
              <Bar dataKey="NA" name={SEVERITY_LABEL.NA} stackId="sections" fill={colors.NA.chartHex} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export function QAVerificationsHistoryPage({
  companyCode,
  currentUserEmail,
  currentUserName,
  isCompanyAdmin,
  isDarkMode,
  onNavigateToEmployeeFix,
}: {
  companyCode?: string;
  currentUserEmail?: string;
  currentUserName?: string;
  isCompanyAdmin?: boolean;
  isDarkMode?: boolean;
  /** Jump from a historical report row to the employee profile tab where the issue can be fixed. */
  onNavigateToEmployeeFix?: (opts: {
    employeeId: string;
    companyCode: string;
    tab: string;
    item: VerificationItem;
  }) => void;
}) {
  const dark = !!isDarkMode;
  const colors = dark ? SEVERITY_COLORS_DARK : SEVERITY_COLORS;
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [derogations, setDerogations] = useState<DerogationRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState<"all" | "fail" | "advisory" | "pass">("all");
  const [openReport, setOpenReport] = useState<VerificationReport | null>(null);
  const [openLoading, setOpenLoading] = useState(false);
  const [bulkPercent, setBulkPercent] = useState<number>(25);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkMessage, setBulkMessage] = useState<string | null>(null);
  const [bulkResult, setBulkResult] = useState<BulkRunResult | null>(null);
  const [clearingAll, setClearingAll] = useState(false);
  const [deletingRunId, setDeletingRunId] = useState<string | null>(null);
  const [printingTile, setPrintingTile] = useState<string | null>(null);
  const [sectionBreakdownBars, setSectionBreakdownBars] = useState<SectionBreakdownBar[]>([]);
  const [sectionBreakdownLoading, setSectionBreakdownLoading] = useState(false);

  const canManageRuns = !!isCompanyAdmin;

  const load = useCallback(() => {
    if (!companyCode) return;
    setLoading(true);
    setError(null);
    const headers = {
      "X-Company-Code": companyCode,
      ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
    };
    Promise.all([
      fetch(`/api/companies/${encodeURIComponent(companyCode)}/verification/runs?companyCode=${encodeURIComponent(companyCode)}`, {
        headers,
      }),
      fetch(`/api/companies/${encodeURIComponent(companyCode)}/assessment-derogations?companyCode=${encodeURIComponent(companyCode)}`, {
        headers,
      }),
    ])
      .then(async ([runsRes, derogationsRes]) => {
        const runsData = await runsRes.json().catch(() => ({}));
        const derogationsData = await derogationsRes.json().catch(() => ({}));
        if (!runsRes.ok) throw new Error(runsData.error || `Failed (${runsRes.status})`);
        if (!derogationsRes.ok) throw new Error(derogationsData.error || `Failed to load derogations (${derogationsRes.status})`);
        setRuns(Array.isArray(runsData.runs) ? (runsData.runs as RunRow[]) : []);
        setDerogations(Array.isArray(derogationsData.derogations) ? (derogationsData.derogations as DerogationRecord[]) : []);
      })
      .catch((e) => setError(e instanceof Error ? e.message : translateUi("Failed to load runs")))
      .finally(() => setLoading(false));
  }, [companyCode, currentUserEmail]);

  useEffect(() => {
    load();
  }, [load]);

  const openHistoricalReport = useCallback(
    async (row: RunRow) => {
      if (!companyCode) return;
      setOpenLoading(true);
      try {
        const res = await fetch(
          `/api/employees/${encodeURIComponent(row.employeeId)}/verification/runs/${encodeURIComponent(row.runId)}?companyCode=${encodeURIComponent(companyCode)}`,
          {
            headers: {
              "X-Company-Code": companyCode,
              ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
            },
          },
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `Failed (${res.status})`);
        setOpenReport(data.report as VerificationReport);
      } catch (e) {
        setError(e instanceof Error ? e.message : translateUi("Failed to load report"));
      } finally {
        setOpenLoading(false);
      }
    },
    [companyCode, currentUserEmail],
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return runs.filter((r) => {
      if (severityFilter === "fail" && r.overall !== "fail") return false;
      if (severityFilter === "advisory" && r.overall !== "advisory") return false;
      if (severityFilter === "pass" && r.overall !== "pass") return false;
      if (!q) return true;
      const blob = `${r.employeeName} ${r.employeeRole ?? ""} ${r.employeeDepot ?? ""} ${r.runByName ?? ""} ${r.runByEmail ?? ""}`.toLowerCase();
      return blob.includes(q);
    });
  }, [runs, filter, severityFilter]);

  const derogationsByEmployee = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of derogations) {
      const employeeId = String(d.employee_id ?? "").trim();
      if (!employeeId) continue;
      map.set(employeeId, (map.get(employeeId) ?? 0) + 1);
    }
    return map;
  }, [derogations]);

  const employeeNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const run of runs) {
      if (!map.has(run.employeeId)) {
        map.set(run.employeeId, run.employeeName || run.employeeId);
      }
    }
    return map;
  }, [runs]);

  const complianceStats = useMemo(() => {
    const source = latestRunPerEmployee(filtered);
    const employeeIdsInView = new Set(source.map((run) => run.employeeId));
    const derogationsInView = derogations.filter((d) => employeeIdsInView.has(String(d.employee_id ?? "")));
    const totalRuns = source.length;
    const outcomes = { pass: 0, advisory: 0, fail: 0 };
    const counts: VerificationCounts = { C: 0, A: 0, R: 0, NA: 0 };
    for (const run of source) {
      if (run.overall === "pass") outcomes.pass += 1;
      else if (run.overall === "advisory") outcomes.advisory += 1;
      else outcomes.fail += 1;
      counts.C += Number(run.counts?.C ?? 0);
      counts.A += Number(run.counts?.A ?? 0);
      counts.R += Number(run.counts?.R ?? 0);
      counts.NA += Number(run.counts?.NA ?? 0);
    }
    const totalChecks = counts.C + counts.A + counts.R + counts.NA;
    const compliantRate = totalChecks > 0 ? (counts.C / totalChecks) * 100 : 0;
    const passRate = totalRuns > 0 ? (outcomes.pass / totalRuns) * 100 : 0;
    return {
      totalRuns,
      outcomes,
      counts,
      totalChecks,
      compliantRate,
      passRate,
      dedupedRuns: source,
      derogationCount: derogationsInView.length,
      derogationsInView,
    };
  }, [filtered, derogations]);

  useEffect(() => {
    const rows = complianceStats.dedupedRuns;
    if (!companyCode || rows.length === 0) {
      setSectionBreakdownBars([]);
      setSectionBreakdownLoading(false);
      return;
    }
    let cancelled = false;
    setSectionBreakdownLoading(true);
    Promise.all(
      rows.map((row) => fetchVerificationReportForPrint(companyCode, row, currentUserEmail).catch(() => null)),
    )
      .then((reports) => {
        if (cancelled) return;
        const valid = reports.filter((report): report is VerificationReport => report !== null);
        setSectionBreakdownBars(buildAggregateSectionBars(valid));
      })
      .finally(() => {
        if (!cancelled) setSectionBreakdownLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyCode, currentUserEmail, complianceStats.dedupedRuns]);

  const handlePrintSeverityTile = useCallback(
    async (sev: VerificationSeverity) => {
      if (!companyCode || printingTile) return;
      setPrintingTile(sev);
      setError(null);
      try {
        await printComplianceSeveritySummary(sev, complianceStats.dedupedRuns, {
          companyCode,
          currentUserEmail,
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : translateUi("Print failed"));
      } finally {
        setPrintingTile(null);
      }
    },
    [companyCode, currentUserEmail, complianceStats.dedupedRuns, printingTile],
  );

  const handlePrintDerogationsTile = useCallback(() => {
    if (printingTile) return;
    setPrintingTile("derogations");
    setError(null);
    try {
      printDerogationsSummary(complianceStats.derogationsInView, employeeNameById, { companyCode });
    } catch (e) {
      setError(e instanceof Error ? e.message : translateUi("Print failed"));
    } finally {
      setPrintingTile(null);
    }
  }, [companyCode, complianceStats.derogationsInView, employeeNameById, printingTile]);

  const runBulkSample = useCallback(async () => {
    if (!companyCode || bulkRunning) return;
    setBulkRunning(true);
    setBulkMessage(null);
    setBulkResult(null);
    setError(null);
    try {
      const res = await fetch(
        `/api/companies/${encodeURIComponent(companyCode)}/verification/runs/bulk?companyCode=${encodeURIComponent(companyCode)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Company-Code": companyCode,
            ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
            ...((currentUserName || "").trim() && !(currentUserName || "").includes("@")
              ? { "X-User-Name": (currentUserName || "").trim() }
              : {}),
          },
          body: JSON.stringify({ percentage: bulkPercent }),
        },
      );
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        percentage?: number;
        totalEmployees?: number;
        selectedEmployees?: number;
        completedRuns?: number;
        failedRuns?: number;
        sampled?: BulkSampledEmployee[];
      };
      if (!res.ok) throw new Error(data.error || `Bulk run failed (${res.status})`);
      setBulkResult({
        percentage: Number(data.percentage ?? bulkPercent),
        totalEmployees: Number(data.totalEmployees ?? 0),
        selectedEmployees: Number(data.selectedEmployees ?? 0),
        completedRuns: Number(data.completedRuns ?? 0),
        failedRuns: Number(data.failedRuns ?? 0),
        sampled: Array.isArray(data.sampled) ? data.sampled : [],
      });
      setBulkMessage(
        `Bulk run complete: ${data.completedRuns ?? 0}/${data.selectedEmployees ?? 0} created` +
          `${(data.failedRuns ?? 0) > 0 ? ` (${data.failedRuns} failed)` : ""}.`,
      );
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : translateUi("Bulk run failed"));
    } finally {
      setBulkRunning(false);
    }
  }, [bulkPercent, bulkRunning, companyCode, currentUserEmail, currentUserName, load]);

  const clearAllRuns = useCallback(async () => {
    if (!companyCode || clearingAll) return;
    const confirmed = window.confirm(
      translateUi("Delete all QA verification runs for this company? This cannot be undone."),
    );
    if (!confirmed) return;
    setClearingAll(true);
    setError(null);
    setBulkMessage(null);
    try {
      const res = await fetch(
        `/api/companies/${encodeURIComponent(companyCode)}/verification/runs?companyCode=${encodeURIComponent(companyCode)}`,
        {
          method: "DELETE",
          headers: {
            "X-Company-Code": companyCode,
            ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
          },
        },
      );
      const data = (await res.json().catch(() => ({}))) as { error?: string; deletedCount?: number };
      if (!res.ok) throw new Error(data.error || translateUi("Delete failed ({{status}})", { status: res.status }));
      setBulkResult(null);
      setBulkMessage(`Deleted ${Number(data.deletedCount ?? 0)} verification run(s).`);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : translateUi("Failed to delete verification runs"));
    } finally {
      setClearingAll(false);
    }
  }, [clearingAll, companyCode, currentUserEmail, load]);

  const deleteRun = useCallback(
    async (row: RunRow) => {
      if (!companyCode || !canManageRuns || deletingRunId) return;
      const confirmed = window.confirm(
        translateUi("Delete this verification run for {{name}}? This cannot be undone.", {
          name: row.employeeName || row.employeeId,
        }),
      );
      if (!confirmed) return;
      setDeletingRunId(row.runId);
      setError(null);
      try {
        const res = await fetch(
          `/api/employees/${encodeURIComponent(row.employeeId)}/verification/runs/${encodeURIComponent(row.runId)}?companyCode=${encodeURIComponent(companyCode)}`,
          {
            method: "DELETE",
            headers: {
              "X-Company-Code": companyCode,
              ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
            },
          },
        );
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        if (!res.ok) throw new Error(data.error || translateUi("Delete failed ({{status}})", { status: res.status }));
        setBulkMessage("Verification run deleted.");
        load();
      } catch (e) {
        setError(e instanceof Error ? e.message : translateUi("Failed to delete verification run"));
      } finally {
        setDeletingRunId(null);
      }
    },
    [canManageRuns, companyCode, currentUserEmail, deletingRunId, load],
  );

  const sampledStats = useMemo(() => {
    if (!bulkResult) return null;
    const sampled = bulkResult.sampled.filter((x) => x.status === "completed");
    if (sampled.length === 0) {
      return {
        totals: { C: 0, A: 0, R: 0, NA: 0 },
        totalChecks: 0,
        complianceRate: 0,
        donutData: [
          { name: "Compliant", value: 0, sev: "C" as const },
          { name: "Advisory", value: 0, sev: "A" as const },
          { name: "Review", value: 0, sev: "R" as const },
          { name: "N/A", value: 0, sev: "NA" as const },
        ],
      };
    }
    const totals = sampled.reduce(
      (acc, x) => {
        acc.C += Number(x.counts?.C ?? 0);
        acc.A += Number(x.counts?.A ?? 0);
        acc.R += Number(x.counts?.R ?? 0);
        acc.NA += Number(x.counts?.NA ?? 0);
        return acc;
      },
      { C: 0, A: 0, R: 0, NA: 0 },
    );
    const totalChecks = totals.C + totals.A + totals.R + totals.NA;
    const complianceRate = totalChecks > 0 ? (totals.C / totalChecks) * 100 : 0;
    return {
      totals,
      totalChecks,
      complianceRate,
      donutData: [
        { name: "Compliant", value: totals.C, sev: "C" as const },
        { name: "Advisory", value: totals.A, sev: "A" as const },
        { name: "Review", value: totals.R, sev: "R" as const },
        { name: "N/A", value: totals.NA, sev: "NA" as const },
      ],
    };
  }, [bulkResult]);

  const severityBox = (sev: VerificationSeverity, value: number) => (
    <span
      key={sev}
      className={cn(
        "inline-flex min-w-[2.2rem] items-center justify-center rounded-md px-2 py-1 text-[11px] font-semibold ring-1 tabular-nums",
        colors[sev].bg,
        colors[sev].fg,
        colors[sev].ring,
      )}
      title={translateUi("{{label}}: {{value}}", { label: SEVERITY_LABEL[sev], value })}
    >
      {sev}: {value}
    </span>
  );

  const derogationBox = (value: number) => (
    <span
      className={cn(
        "inline-flex min-w-[2.2rem] items-center justify-center rounded-md px-2 py-1 text-[11px] font-semibold ring-1 tabular-nums",
        dark ? "bg-violet-500/15 text-violet-300 ring-violet-500/30" : "bg-violet-50 text-violet-700 ring-violet-200",
      )}
      title={translateUi("Derogations: {{value}}", { value })}
    >
      D: {value}
    </span>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:flex-wrap sm:gap-3">
        <div className="min-w-0">
          <h2 className={cn("text-2xl font-bold", dark ? "text-slate-100" : "text-slate-900")}>{translateUi("QA Verifications")}</h2>
          <p className={cn("text-sm", dark ? "text-slate-400" : "text-slate-500")}>
            Compliance verification history across the company. Run a new verification from any employee record.
          </p>
        </div>
        <div className="flex flex-col gap-2 w-full min-w-0 sm:w-auto sm:flex-row sm:items-center sm:flex-wrap">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search employee or assessor"
            className={cn(
              "px-3 py-2 rounded-xl text-sm border w-full min-w-0 sm:w-64",
              dark
                ? "bg-slate-900 border-slate-700 text-slate-100 placeholder-slate-500"
                : "bg-white border-slate-300 text-slate-800 placeholder-slate-400",
            )}
          />
          <div className="flex items-center gap-2 w-full min-w-0 sm:w-auto">
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value as typeof severityFilter)}
              className={cn(
                "px-3 py-2 rounded-xl text-sm border flex-1 min-w-0 sm:flex-none",
                dark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800",
              )}
            >
              <option value="all">All outcomes</option>
              <option value="fail"> Failed </option>
              <option value="advisory"> Advisory </option>
              <option value="pass"> Passed </option>
            </select>
            <button
              type="button"
              onClick={clearAllRuns}
              disabled={!companyCode || clearingAll || loading || !canManageRuns}
              className={cn(
                "px-3 py-2 rounded-xl text-sm border font-medium transition-colors shrink-0 whitespace-nowrap",
                canManageRuns
                  ? dark
                    ? "border-rose-500/40 text-rose-300 hover:bg-rose-500/10 disabled:opacity-50"
                    : "border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                  : "hidden",
              )}
            >
              {clearingAll ? translateUi("Clearing...") : translateUi("Clear all")}
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className={cn("rounded-2xl p-3 text-sm", dark ? "bg-rose-500/10 text-rose-300" : "bg-red-50 text-red-700 border border-red-100")}>
          {error}
        </div>
      ) : null}

      <div
        className={cn(
          "rounded-3xl border shadow-sm p-4 sm:p-5",
          dark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200",
        )}
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={cn("text-xs font-semibold uppercase tracking-wider", dark ? "text-slate-400" : "text-slate-500")}>
              Overall compliance
            </p>
            <p className={cn("mt-1 text-lg font-bold", dark ? "text-slate-100" : "text-slate-900")}>
              {complianceStats.compliantRate.toFixed(1)}% compliant checks
            </p>
            <p className={cn("text-xs mt-1", dark ? "text-slate-400" : "text-slate-500")}>
              {complianceStats.totalRuns} unique employee{complianceStats.totalRuns === 1 ? "" : "s"} (latest run each) · {complianceStats.totalChecks} total checks · pass rate {complianceStats.passRate.toFixed(1)}%
            </p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
          {(["C", "A", "R", "NA"] as VerificationSeverity[]).map((sev) => (
            <div key={sev} className={cn("relative rounded-xl p-2.5 ring-1", colors[sev].bg, colors[sev].ring)}>
              <button
                type="button"
                onClick={() => void handlePrintSeverityTile(sev)}
                disabled={!!printingTile || complianceStats.counts[sev] === 0}
                className={cn(
                  "absolute top-2 right-2 rounded-md p-1 transition-colors",
                  dark ? "text-slate-400 hover:bg-slate-800/80 hover:text-slate-200" : "text-slate-500 hover:bg-white/80 hover:text-slate-700",
                  printingTile === sev && "opacity-60",
                  (printingTile || complianceStats.counts[sev] === 0) && "cursor-not-allowed opacity-50",
                )}
                title={translateUi("Print {{label}} items", { label: SEVERITY_LABEL[sev] })}
                aria-label={translateUi("Print {{label}} items", { label: SEVERITY_LABEL[sev] })}
              >
                <Printer className="h-3.5 w-3.5" />
              </button>
              <p className={cn("text-[11px] font-semibold uppercase tracking-wide pr-7", colors[sev].fg)}>{SEVERITY_LABEL[sev]}</p>
              <p className={cn("mt-1 text-lg font-bold tabular-nums", colors[sev].fg)}>{complianceStats.counts[sev]}</p>
            </div>
          ))}
          <div
            className={cn(
              "relative rounded-xl p-2.5 ring-1",
              dark ? "bg-violet-500/15 ring-violet-500/30" : "bg-violet-50 ring-violet-200",
            )}
          >
            <button
              type="button"
              onClick={handlePrintDerogationsTile}
              disabled={!!printingTile || complianceStats.derogationCount === 0}
              className={cn(
                "absolute top-2 right-2 rounded-md p-1 transition-colors",
                dark ? "text-violet-300 hover:bg-violet-500/20" : "text-violet-600 hover:bg-violet-100",
                printingTile === "derogations" && "opacity-60",
                (printingTile || complianceStats.derogationCount === 0) && "cursor-not-allowed opacity-50",
              )}
              title={translateUi("Print derogation records")}
              aria-label={translateUi("Print derogation records")}
            >
              <Printer className="h-3.5 w-3.5" />
            </button>
            <p className={cn("text-[11px] font-semibold uppercase tracking-wide pr-7", dark ? "text-violet-300" : "text-violet-700")}> Derogations </p>
            <p className={cn("mt-1 text-lg font-bold tabular-nums", dark ? "text-violet-300" : "text-violet-700")}>
              {complianceStats.derogationCount}
            </p>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <span className={cn("rounded-md px-2 py-1 ring-1", dark ? "bg-emerald-500/10 text-emerald-300 ring-emerald-500/30" : "bg-emerald-50 text-emerald-700 ring-emerald-200")}>
            Passed: {complianceStats.outcomes.pass}
          </span>
          <span className={cn("rounded-md px-2 py-1 ring-1", dark ? "bg-amber-500/10 text-amber-300 ring-amber-500/30" : "bg-amber-50 text-amber-700 ring-amber-200")}>
            Advisory: {complianceStats.outcomes.advisory}
          </span>
          <span className={cn("rounded-md px-2 py-1 ring-1", dark ? "bg-rose-500/10 text-rose-300 ring-rose-500/30" : "bg-rose-50 text-rose-700 ring-rose-200")}>
            Failed: {complianceStats.outcomes.fail}
          </span>
        </div>
        <ComplianceSectionBreakdownChart
          bars={sectionBreakdownBars}
          dark={dark}
          loading={sectionBreakdownLoading}
          employeeCount={complianceStats.totalRuns}
        />
        {bulkMessage ? (
          <p className={cn("mt-3 text-xs", dark ? "text-emerald-300" : "text-emerald-700")}>{bulkMessage}</p>
        ) : null}
      </div>

      <div
        className={cn(
          "rounded-3xl border shadow-sm p-4 sm:p-5",
          dark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200",
        )}
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className={cn("text-xs font-semibold uppercase tracking-wider", dark ? "text-slate-400" : "text-slate-500")}>
              Sampled run analysis
            </p>
            <p className={cn("mt-1 text-sm", dark ? "text-slate-300" : "text-slate-700")}>
              Run verification for a selected percentage and review sampled compliance instantly.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className={cn("block text-[11px] mb-1 font-medium", dark ? "text-slate-400" : "text-slate-500")}>
                Run on sample of employees
              </label>
              <select
                value={bulkPercent}
                onChange={(e) => setBulkPercent(Number(e.target.value))}
                className={cn(
                  "px-3 py-2 rounded-xl text-sm border",
                  dark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800",
                )}
              >
                {[5, 10, 20, 25, 50, 75, 100].map((p) => (
                  <option key={p} value={p}>
                    {p}%
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={runBulkSample}
              disabled={!companyCode || bulkRunning}
              className={cn(
                "px-4 py-2 rounded-xl text-sm font-medium transition-colors",
                bulkRunning
                  ? "bg-slate-400 text-white cursor-not-allowed"
                  : "bg-indigo-600 text-white hover:bg-indigo-700",
              )}
            >
              {bulkRunning ? translateUi("Running...") : translateUi("Run selected %")}
            </button>
          </div>
        </div>

        {bulkResult ? (
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-[18rem_minmax(0,1fr)] gap-4">
            <div className={cn("rounded-2xl ring-1 p-3 relative", dark ? "ring-slate-700" : "ring-slate-200")}>
              <p className={cn("text-xs font-semibold uppercase tracking-wider", dark ? "text-slate-400" : "text-slate-500")}>
                Sample compliance
              </p>
              <div className="qa-chart-host relative mt-2 h-52 min-h-[13rem]">
                <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 text-center">
                  <p className={cn("text-2xl font-bold tabular-nums leading-none", dark ? "text-slate-100" : "text-slate-900")}>
                    {sampledStats.complianceRate.toFixed(1)}%
                  </p>
                  <p className={cn("mt-1 text-[10px] font-semibold uppercase tracking-wider", dark ? "text-slate-500" : "text-slate-500")}>
                    compliant
                  </p>
                </div>
                {sampledStats.totalChecks > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={sampledStats.donutData} dataKey="value" nameKey="name" innerRadius={44} outerRadius={80} isAnimationActive>
                        {sampledStats.donutData.map((entry) => (
                          <Cell key={entry.sev} fill={colors[entry.sev].chartHex} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={dark ? { background: "#0f172a", border: "1px solid #334155", color: "#e2e8f0" } : undefined}
                        formatter={(value: number, name: string) => [`${value} checks`, name]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className={cn("h-full w-full flex items-center justify-center text-xs text-center px-6", dark ? "text-slate-400" : "text-slate-500")}>
                    No completed sampled reports yet.
                  </div>
                )}
              </div>
              <p className={cn("mt-1 text-xs", dark ? "text-slate-400" : "text-slate-500")}>
                {bulkResult.completedRuns}/{bulkResult.selectedEmployees} sampled completed ({bulkResult.percentage}% target)
              </p>
            </div>
            <div className={cn("rounded-2xl ring-1 p-3", dark ? "ring-slate-700" : "ring-slate-200")}>
              <p className={cn("text-xs font-semibold uppercase tracking-wider mb-2", dark ? "text-slate-400" : "text-slate-500")}>
                Sampled employees
              </p>
              <div className="flex flex-wrap gap-2.5">
                {bulkResult.sampled.map((emp) => {
                  const outcomeColor =
                    emp.overall === "pass"
                      ? dark
                        ? "bg-emerald-500/10 ring-emerald-500/30 text-emerald-300"
                        : "bg-emerald-50 ring-emerald-200 text-emerald-700"
                      : emp.overall === "advisory"
                        ? dark
                          ? "bg-amber-500/10 ring-amber-500/30 text-amber-300"
                          : "bg-amber-50 ring-amber-200 text-amber-700"
                        : dark
                          ? "bg-rose-500/10 ring-rose-500/30 text-rose-300"
                          : "bg-rose-50 ring-rose-200 text-rose-700";
                  return (
                    <div key={`${emp.employeeId}-${emp.runId ?? "failed"}`} className={cn("rounded-xl px-3 py-2 ring-1 min-w-0 sm:min-w-[10rem] max-w-full", outcomeColor)}>
                      <p className="text-xs font-semibold truncate">{emp.employeeName || emp.employeeId}</p>
                      <p className="text-[11px] mt-0.5">
                        {emp.status === "completed" ? `Compliance ${emp.complianceRate.toFixed(1)}%` : `Run failed${emp.error ? `: ${emp.error}` : ""}`}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <p className={cn("mt-4 text-sm italic", dark ? "text-slate-400" : "text-slate-500")}>
            Run a sample to view donut analytics and highlighted sampled employees.
          </p>
        )}
      </div>

      <div
        className={cn(
          "rounded-3xl border shadow-sm overflow-hidden min-w-0",
          dark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200",
        )}
      >
        {loading ? (
          <div className="p-12 text-center">
            <div className={cn("inline-block h-8 w-8 animate-spin rounded-full border-b-2", dark ? "border-indigo-400" : "border-indigo-600")} />
          </div>
        ) : filtered.length === 0 ? (
          <div className={cn("p-12 text-center text-sm", dark ? "text-slate-400" : "text-slate-500")}>
            No verification runs yet. Open an employee record and click <span className="font-semibold">Run Employee Verification</span>.
          </div>
        ) : (
          <>
          {/* Mobile: stacked cards — no horizontal scroll */}
          <div className={cn("md:hidden divide-y", dark ? "divide-slate-800" : "divide-slate-100")}>
            {filtered.map((row) => {
              const verdict = overallVerdict(row.overall as VerificationOverall);
              const derogationCount = derogationsByEmployee.get(row.employeeId) ?? 0;
              return (
                <div key={row.runId} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className={cn("font-semibold truncate", dark ? "text-slate-100" : "text-slate-900")}>
                        {row.employeeName || row.employeeId}
                      </div>
                      <div className={cn("text-xs mt-0.5", dark ? "text-slate-400" : "text-slate-500")}>
                        {row.employeeRole}
                        {row.employeeDepot ? ` · ${row.employeeDepot}` : ""}
                      </div>
                    </div>
                    <span className="text-xs font-semibold shrink-0" style={{ color: verdict.color }}>
                      {verdict.label}
                    </span>
                  </div>
                  <div className={cn("grid grid-cols-1 gap-1.5 text-xs", dark ? "text-slate-300" : "text-slate-600")}>
                    <div>
                      <span className={cn("uppercase tracking-wider font-semibold mr-1.5", dark ? "text-slate-500" : "text-slate-400")}>
                        {translateUi("Run at")}
                      </span>
                      {formatDateTime(row.runAt)}
                    </div>
                    <div>
                      <span className={cn("uppercase tracking-wider font-semibold mr-1.5", dark ? "text-slate-500" : "text-slate-400")}>
                        {translateUi("Verifier")}
                      </span>
                      {row.runByName || row.runByEmail || "—"}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {severityBox("C", row.counts.C)}
                    {severityBox("A", row.counts.A)}
                    {severityBox("R", row.counts.R)}
                    {severityBox("NA", row.counts.NA)}
                    {derogationBox(derogationCount)}
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-1">
                    {canManageRuns ? (
                      <button
                        type="button"
                        onClick={() => deleteRun(row)}
                        disabled={deletingRunId === row.runId}
                        className={cn(
                          "rounded-md p-1.5 transition-colors disabled:opacity-50",
                          dark ? "text-rose-400 hover:bg-rose-500/10" : "text-rose-600 hover:bg-rose-50",
                        )}
                        title={translateUi("Delete run")}
                        aria-label={translateUi("Delete run")}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => openHistoricalReport(row)}
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-700 px-2 py-1"
                    >
                      {translateUi("Open")}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop / tablet: table */}
          <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm min-w-[40rem]">
            <thead className={cn("text-xs uppercase tracking-wider", dark ? "bg-slate-800/60 text-slate-400" : "bg-slate-50 text-slate-500")}>
              <tr>
                <th className="text-left px-4 py-3"> Employee </th>
                <th className="text-left px-4 py-3">Run at</th>
                <th className="text-left px-4 py-3">Verifier</th>
                <th className="text-left px-4 py-3"> Outcome </th>
                <th className="text-left px-4 py-3">
                  <div>C / A / R / NA / D</div>
                  <SeverityLegendKey dark={dark} layout="stacked" />
                </th>
                <th className="text-right px-4 py-3"> </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const verdict = overallVerdict(row.overall as VerificationOverall);
                const derogationCount = derogationsByEmployee.get(row.employeeId) ?? 0;
                return (
                  <tr
                    key={row.runId}
                    className={cn(
                      "border-t",
                      dark ? "border-slate-800 hover:bg-slate-800/40" : "border-slate-100 hover:bg-slate-50",
                    )}
                  >
                    <td className="px-4 py-3">
                      <div className={cn("font-semibold", dark ? "text-slate-100" : "text-slate-900")}>
                        {row.employeeName || row.employeeId}
                      </div>
                      <div className={cn("text-xs", dark ? "text-slate-400" : "text-slate-500")}>
                        {row.employeeRole}
                        {row.employeeDepot ? ` · ${row.employeeDepot}` : ""}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(row.runAt)}</td>
                    <td className={cn("px-4 py-3", dark ? "text-slate-300" : "text-slate-700")}>
                      {row.runByName || row.runByEmail || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold" style={{ color: verdict.color }}>
                        {verdict.label}
                      </span>
                    </td>
                    <td className={cn("px-4 py-3", dark ? "text-slate-300" : "text-slate-700")}>
                      <div className="flex flex-wrap gap-1.5">
                        {severityBox("C", row.counts.C)}
                        {severityBox("A", row.counts.A)}
                        {severityBox("R", row.counts.R)}
                        {severityBox("NA", row.counts.NA)}
                        {derogationBox(derogationCount)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        {canManageRuns ? (
                          <button
                            type="button"
                            onClick={() => deleteRun(row)}
                            disabled={deletingRunId === row.runId}
                            className={cn(
                              "rounded-md p-1 transition-colors disabled:opacity-50",
                              dark ? "text-rose-400 hover:bg-rose-500/10" : "text-rose-600 hover:bg-rose-50",
                            )}
                            title={translateUi("Delete run")}
                            aria-label={translateUi("Delete run")}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => openHistoricalReport(row)}
                          className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
                        > Open </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          </>
        )}
      </div>

      {openLoading || openReport ? (
        <div className="fixed inset-0 z-[60] overflow-y-auto overflow-x-hidden bg-slate-900/60 backdrop-blur-sm">
          <div className="min-h-full flex items-start justify-center p-2 sm:p-4 lg:p-8">
            <div className="w-full max-w-6xl min-w-0">
              <div
                className={cn(
                  "rounded-2xl sm:rounded-3xl shadow-2xl border p-3 sm:p-5 lg:p-8 min-w-0 max-w-full overflow-x-hidden",
                  dark ? "bg-slate-900 border-slate-700" : "bg-slate-50 border-slate-200",
                )}
              >
                {openReport ? (
                  <VerificationReportView
                    report={openReport}
                    isDarkMode={dark}
                    onClose={() => setOpenReport(null)}
                    onNavigateToFix={
                      onNavigateToEmployeeFix
                        ? (target) => {
                            onNavigateToEmployeeFix({
                              employeeId: openReport.employeeId,
                              companyCode: openReport.companyCode,
                              tab: target.tab,
                              item: target.item,
                            });
                            setOpenReport(null);
                          }
                        : undefined
                    }
                  />
                ) : (
                  <div className="flex justify-center py-12">
                    <div className={cn("h-10 w-10 animate-spin rounded-full border-b-2", dark ? "border-indigo-400" : "border-indigo-600")} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
